<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsappCampaignsAPI from 'dashboard/api/whatsappCampaigns';
import { conversionTypesApi } from 'dashboard/api/conversions';
import PipelinesAPI from 'dashboard/api/pipelines';

const { t, te } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const accountLabels = useMapGetter('labels/getLabels');
const isCreateMode = computed(() => route.name === 'campaigns_whatsapp_new');

const loading = ref(false);
const creating = ref(false);
const metricsLoading = ref(false);
const recipientsLoading = ref(false);
const eventsLoading = ref(false);

const filters = reactive({
  inboxId: '',
  status: '',
  q: '',
});

const campaigns = ref([]);
const selectedCampaignId = ref(null);
const selectedCampaign = computed(() =>
  campaigns.value.find(campaign => campaign.id === selectedCampaignId.value)
);

const metrics = ref(null);
const summary = ref({ totals: {} });
const recipients = ref([]);
const recipientsMeta = ref({ current_page: 1, total_pages: 1, total_count: 0 });
const events = ref([]);
const settingsModalOpen = ref(false);
const savingSettings = ref(false);
const cloningCampaign = ref(false);
const deletingCampaignId = ref(null);
const hydratingSettingsForm = ref(false);
const settingsForm = reactive({
  campaignId: null,
  name: '',
  status: '',
  audience_type: 'upload',
  audience_filters: {},
  import_inbox_id: '',
  filter_labels: '',
  pipeline_id: '',
  pipeline_stage_id: '',
  conversion_type_id: '',
  conversion_source_type: '',
  conversion_match_rule: 'any',
  conversion_converted_after: '',
  conversion_converted_before: '',
  conversion_days_since_min: '',
  conversion_days_since_max: '',
  conversion_min_value: '',
  conversion_max_value: '',
  batch_size: 50,
  delay_type: 'fixed',
  delay_seconds: 2,
  delay_min_seconds: 1,
  delay_max_seconds: 5,
  window_start: '09:00',
  window_end: '18:00',
  retry_max_attempts: 3,
  retry_backoff_seconds: 30,
  webhook_url: '',
  delivery_label_title: '',
});
const LIVE_POLL_MS = 5000;
let livePollTimer = null;

const wizardOpen = ref(false);
const wizardStep = ref(1);
const wizard = reactive({
  name: '',
  inbox_id: '',
  template_id: '',
  audience_type: 'upload',
  send_now: true,
  scheduled_at: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  batch_size: 50,
  delay_type: 'fixed',
  delay_seconds: 2,
  delay_min_seconds: 1,
  delay_max_seconds: 5,
  window_start: '09:00',
  window_end: '18:00',
  retry_max_attempts: 3,
  retry_backoff_seconds: 30,
  webhook_url: '',
  delivery_label_title: '',
  upload_file: null,
  upload_phone_column: 'phone',
  import_inbox_id: '',
  pipeline_id: '',
  pipeline_stage_id: '',
  filter_labels: '',
  conversion_type_id: '',
  conversion_source_type: '',
  conversion_match_rule: 'any',
  conversion_converted_after: '',
  conversion_converted_before: '',
  conversion_days_since_min: '',
  conversion_days_since_max: '',
  conversion_min_value: '',
  conversion_max_value: '',
});

const inboxTemplates = ref([]);
const syncingTemplates = ref(false);
const audienceFields = ref([]);
const pipelinesLoading = ref(false);
const pipelines = ref([]);
const pipelineStages = ref([]);
const conversionTypes = ref([]);
const wizardExclusions = ref([]);
const exclusionStagesMap = ref({});
const settingsExclusions = ref([]);
const settingsExclusionStagesMap = ref({});
const uploadColumns = ref([]);
const uploadSampleRow = ref({});
const variableMappings = reactive({});
const previewLoading = ref(false);
const previewData = ref(null);
const audienceValidation = ref(null);
const uploadingMediaKey = ref(null);
const mediaInputRefs = ref({});
const wizardNextAttempted = ref(false);

const MEDIA_VALUE_TYPES = ['image', 'video', 'audio', 'document'];

const statusOptions = [
  'draft',
  'scheduled',
  'running',
  'paused',
  'finished',
  'cancelled',
  'failed',
];

const conversionSourceOptions = [
  {
    value: '',
    labelKey: 'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_SOURCE_ALL',
  },
  {
    value: 'PipelineItem',
    labelKey: 'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_SOURCE_CRM',
  },
  {
    value: 'AppointmentBooking',
    labelKey: 'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_SOURCE_AGENDA',
  },
];

const conversionMatchRuleOptions = [
  {
    value: 'any',
    labelKey: 'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_MATCH_ANY',
  },
  {
    value: 'last',
    labelKey: 'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_MATCH_LAST',
  },
];

const humanizeValue = value =>
  String(value || '')
    .replace(/_/g, ' ')
    .trim();

const sourceTypeLabel = sourceType => {
  if (sourceType === 'PipelineItem') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_SOURCE_CRM');
  }
  if (sourceType === 'AppointmentBooking') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_SOURCE_AGENDA');
  }

  return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_SOURCE_ALL');
};

const conversionMatchRuleLabel = matchRule => {
  if (matchRule === 'last') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_MATCH_LAST');
  }

  return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_MATCH_ANY');
};

const translateStatus = status => {
  const key = `CAMPAIGN.WHATSAPP_BROADCASTS.STATUS_LABELS.${String(
    status || ''
  ).toUpperCase()}`;
  return te(key) ? t(key) : humanizeValue(status);
};

const translateEventType = eventType => {
  const key = `CAMPAIGN.WHATSAPP_BROADCASTS.EVENT_TYPES.${String(
    eventType || ''
  ).toUpperCase()}`;
  return te(key) ? t(key) : humanizeValue(eventType);
};

const normalizedComponentType = component =>
  String(component?.type || '')
    .trim()
    .toLowerCase();

const ensureArray = value => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const nonEmptyString = value => {
  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized || '';
  }

  if (Array.isArray(value)) {
    return value.map(nonEmptyString).find(Boolean) || '';
  }

  return '';
};

const toObject = value => {
  if (value && typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
    return null;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const normalizedComponent = component => {
  const parsed = toObject(component);
  return parsed || component;
};

const componentsFromValue = value => {
  const parsedValue = normalizedComponent(value);

  if (Array.isArray(parsedValue)) {
    return parsedValue.map(item => normalizedComponent(item));
  }

  if (parsedValue && typeof parsedValue === 'object') {
    const entries = Object.entries(parsedValue);
    const numericEntries = entries
      .filter(([key]) => /^\d+$/.test(String(key)))
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([, item]) => normalizedComponent(item));

    if (numericEntries.length) return numericEntries;
    return [parsedValue];
  }

  return ensureArray(parsedValue).map(item => normalizedComponent(item));
};

const componentPreviewText = component => {
  const normalized = normalizedComponent(component);
  const directText = [
    normalized?.text,
    normalized?.body,
    normalized?.caption,
    normalized?.url,
    normalized?.payload,
    normalized?.fallback_value,
  ]
    .map(nonEmptyString)
    .find(Boolean);
  if (directText) return directText;

  const exampleText = [
    normalized?.example?.body_text,
    normalized?.example?.header_text,
    normalized?.example?.text,
  ]
    .map(nonEmptyString)
    .find(Boolean);
  if (exampleText) return exampleText;

  return '';
};

const templatePreviewText = template => {
  const components = componentsFromValue(template?.components);
  const bodyComponent = components.find(
    component => normalizedComponentType(component) === 'body'
  );
  const textFromBody = componentPreviewText(bodyComponent);
  if (textFromBody) return textFromBody;

  const fallbackText = components.map(componentPreviewText).find(Boolean);
  if (fallbackText) return fallbackText;

  const variablePositions = ensureArray(template?.variable_schema)
    .map(variable => {
      const explicitPosition = Number(variable?.position);
      if (Number.isFinite(explicitPosition) && explicitPosition > 0) {
        return explicitPosition;
      }

      const key = String(variable?.key || '');
      const match = key.match(/_(\d+)$/);
      return match ? Number(match[1]) : null;
    })
    .filter(position => Number.isFinite(position) && position > 0)
    .sort((a, b) => a - b);

  if (variablePositions.length) {
    return variablePositions.map(position => `{{${position}}}`).join(' ');
  }

  const componentTypes = components
    .map(component =>
      String(component?.type || '')
        .trim()
        .toUpperCase()
    )
    .filter(Boolean);
  if (componentTypes.length) {
    return componentTypes.join(' • ');
  }

  if (template?.components && typeof template.components === 'object') {
    const rawComponents = JSON.stringify(template.components);
    if (rawComponents && rawComponents !== '{}' && rawComponents !== '[]') {
      return rawComponents;
    }
  }

  return '';
};

const templatePreviewExample = template => {
  const components = componentsFromValue(template?.components);
  const bodyComponent = components.find(
    component => normalizedComponentType(component) === 'body'
  );

  const bodyExample = nonEmptyString(bodyComponent?.example?.body_text);
  if (bodyExample) return bodyExample;

  const headerExample = nonEmptyString(bodyComponent?.example?.header_text);
  if (headerExample) return headerExample;

  return components
    .map(component => nonEmptyString(component?.example?.body_text))
    .find(Boolean);
};

const templateVariableCount = template =>
  ensureArray(template?.variable_schema).length;

const campaignTemplateLabel = campaign =>
  t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.TEMPLATE_INFO', {
    name: campaign.template_name,
    language: campaign.template_language,
  });

const eventRowLabel = event =>
  t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.EVENT_ROW', {
    eventType: translateEventType(event.event_type),
    time: new Date(event.created_at * 1000).toLocaleString(),
  });

const wizardSteps = computed(() => [
  {
    id: 1,
    title: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.NAME'),
  },
  {
    id: 2,
    title: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.TEMPLATE'),
  },
  {
    id: 3,
    title: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_TYPE'),
  },
  {
    id: 4,
    title: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_TYPE'),
  },
  {
    id: 5,
    title: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SCHEDULED_AT'),
  },
]);

const wizardProgress = computed(() => {
  const total = wizardSteps.value.length;
  if (total <= 1) return 0;
  return ((wizardStep.value - 1) / (total - 1)) * 100;
});
const wizardStepTitle = computed(
  () => wizardSteps.value.find(step => step.id === wizardStep.value)?.title
);
const selectedTemplate = computed(() =>
  inboxTemplates.value.find(
    template => template.id === Number(wizard.template_id)
  )
);
const templateCards = computed(() =>
  inboxTemplates.value.map(template => ({
    ...template,
    previewText: templatePreviewText(template),
    previewExample: templatePreviewExample(template),
    variableCount: templateVariableCount(template),
    statusLabel: translateStatus(template.status || 'unknown'),
  }))
);
const templateVariables = computed(
  () => selectedTemplate.value?.variable_schema || []
);
const hasTemplateVariables = computed(() => templateVariables.value.length > 0);
const templateVariablesWithMappings = computed(() =>
  templateVariables.value
    .map(variable => ({
      variable,
      mapping: variableMappings[variable.key],
    }))
    .filter(item => !!item.mapping)
);
const mappingFieldOptions = computed(() => {
  if (wizard.audience_type === 'upload') {
    return uploadColumns.value.map(column => ({
      key: column,
      label: column,
    }));
  }

  return audienceFields.value;
});
const isVariableMappingReady = computed(() => {
  if (!hasTemplateVariables.value) return true;

  return templateVariables.value.every(variable => {
    const mapping = variableMappings[variable.key];
    if (!mapping) return false;
    if (mapping.type === 'fixed')
      return (mapping.value || '').trim().length > 0;

    return (mapping.key || '').trim().length > 0;
  });
});
const canPreviewTemplate = computed(() => {
  if (!wizard.inbox_id || !wizard.template_id) return false;
  if (!isVariableMappingReady.value) return false;

  if (wizard.audience_type === 'upload') {
    return !!wizard.upload_file;
  }

  if (wizard.audience_type === 'inbox_contacts') {
    return !!(wizard.import_inbox_id || wizard.inbox_id);
  }

  if (wizard.audience_type === 'pipeline_stage') {
    return !!wizard.pipeline_id && !!wizard.pipeline_stage_id;
  }

  if (wizard.audience_type === 'conversion_filters') {
    return true;
  }

  return true;
});

const metricCards = computed(() => [
  {
    key: 'queued',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.QUEUED'),
    value:
      Number(metrics.value?.totals?.queued || 0) +
      Number(metrics.value?.totals?.sending || 0),
    accent: 'text-n-amber-11',
    bg: 'bg-n-amber-3/30',
  },
  {
    key: 'sent',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.SENT'),
    value: Number(metrics.value?.totals?.sent || 0),
    accent: 'text-n-blue-11',
    bg: 'bg-n-blue-3/30',
  },
  {
    key: 'delivered',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.DELIVERED'),
    value: Number(metrics.value?.totals?.delivered || 0),
    accent: 'text-n-teal-11',
    bg: 'bg-n-teal-3/30',
  },
  {
    key: 'opened',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.OPENED'),
    value: Number(metrics.value?.totals?.read || 0),
    accent: 'text-n-emerald-11',
    bg: 'bg-n-emerald-3/30',
  },
  {
    key: 'failed',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.FAILED'),
    value: Number(metrics.value?.totals?.failed || 0),
    accent: 'text-n-ruby-11',
    bg: 'bg-n-ruby-3/30',
  },
]);

const summaryMetricCards = computed(() => [
  {
    key: 'queued',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.QUEUED'),
    value: summary.value?.totals?.queued || 0,
    accent: 'text-n-amber-11',
    bg: 'bg-n-amber-3/30',
  },
  {
    key: 'sent',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.SENT'),
    value: summary.value?.totals?.sent || 0,
    accent: 'text-n-blue-11',
    bg: 'bg-n-blue-3/30',
  },
  {
    key: 'delivered',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.DELIVERED'),
    value: Number(summary.value?.totals?.delivered || 0),
    accent: 'text-n-teal-11',
    bg: 'bg-n-teal-3/30',
  },
  {
    key: 'opened',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.OPENED'),
    value: Number(summary.value?.totals?.read || 0),
    accent: 'text-n-emerald-11',
    bg: 'bg-n-emerald-3/30',
  },
  {
    key: 'failed',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.FAILED'),
    value: Number(summary.value?.totals?.failed || 0),
    accent: 'text-n-ruby-11',
    bg: 'bg-n-ruby-3/30',
  },
]);

const summaryCampaignCards = computed(() => [
  {
    key: 'running_campaigns',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.RUNNING_CAMPAIGNS'),
    value: Number(summary.value?.campaigns?.running || 0),
    accent: 'text-n-teal-11',
    bg: 'bg-n-teal-3/30',
  },
  {
    key: 'finished_campaigns',
    label: t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.FINISHED_CAMPAIGNS'),
    value: Number(summary.value?.campaigns?.finished || 0),
    accent: 'text-n-slate-12',
    bg: 'bg-n-slate-4/80',
  },
]);

const summaryBaseTotal = computed(() => {
  const totals = summary.value?.totals || {};
  return (
    Number(totals.queued || 0) +
      Number(totals.sent || 0) +
      Number(totals.delivered || 0) +
      Number(totals.failed || 0) || 1
  );
});

const hasRunningCampaign = computed(() =>
  campaigns.value.some(campaign => campaign.status === 'running')
);
const metricBaseTotal = computed(() => {
  const totals = metrics.value?.totals || {};
  return (
    Number(totals.queued || 0) +
      Number(totals.sending || 0) +
      Number(totals.sent || 0) +
      Number(totals.delivered || 0) +
      Number(totals.read || 0) +
      Number(totals.failed || 0) || 1
  );
});

const statusBadgeClass = status => {
  const map = {
    draft: 'bg-n-slate-4 text-n-slate-11',
    scheduled: 'bg-n-blue-3 text-n-blue-11',
    running: 'bg-n-teal-3 text-n-teal-11',
    paused: 'bg-n-amber-3 text-n-amber-11',
    finished: 'bg-n-teal-3 text-n-teal-11',
    cancelled: 'bg-n-slate-4 text-n-slate-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    delivered: 'bg-n-teal-3 text-n-teal-11',
    read: 'bg-n-blue-3 text-n-blue-11',
    sent: 'bg-n-slate-4 text-n-slate-11',
    queued: 'bg-n-slate-4 text-n-slate-11',
    pending: 'bg-n-slate-4 text-n-slate-11',
  };

  return map[status] || 'bg-n-slate-4 text-n-slate-11';
};

const requiresAudienceValidation = computed(
  () => wizard.audience_type !== 'upload'
);

const isAudienceValidationReady = computed(() => {
  if (!requiresAudienceValidation.value) return true;

  return (
    audienceValidation.value?.validated &&
    Number(audienceValidation.value?.count || 0) > 0
  );
});

const wizardAudienceConfigErrors = computed(() => {
  const errors = [];

  if (wizard.audience_type === 'upload' && !wizard.upload_file) {
    errors.push(t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_UPLOAD_FILE'));
  }

  if (wizard.audience_type === 'inbox_contacts' && !wizard.import_inbox_id) {
    errors.push(t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_SOURCE_INBOX'));
  }

  if (
    wizard.audience_type === 'filtered' &&
    !String(wizard.filter_labels || '').trim()
  ) {
    errors.push(t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_FILTER_LABELS'));
  }

  if (wizard.audience_type === 'pipeline_stage' && !wizard.pipeline_id) {
    errors.push(t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_PIPELINE'));
  }

  if (
    wizard.audience_type === 'pipeline_stage' &&
    wizard.pipeline_id &&
    !wizard.pipeline_stage_id
  ) {
    errors.push(t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_STAGE'));
  }

  const daysSinceMin = Number(wizard.conversion_days_since_min || 0);
  const daysSinceMax = Number(wizard.conversion_days_since_max || 0);
  if (
    wizard.audience_type === 'conversion_filters' &&
    wizard.conversion_days_since_min &&
    wizard.conversion_days_since_max &&
    daysSinceMin < daysSinceMax
  ) {
    errors.push(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_CONVERSION_DAYS_RANGE')
    );
  }

  if (
    wizard.audience_type === 'conversion_filters' &&
    wizard.conversion_converted_after &&
    wizard.conversion_converted_before &&
    wizard.conversion_converted_after > wizard.conversion_converted_before
  ) {
    errors.push(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_CONVERSION_DATE_RANGE')
    );
  }

  const minValue = Number(wizard.conversion_min_value || 0);
  const maxValue = Number(wizard.conversion_max_value || 0);
  if (
    wizard.audience_type === 'conversion_filters' &&
    wizard.conversion_min_value &&
    wizard.conversion_max_value &&
    minValue > maxValue
  ) {
    errors.push(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_CONVERSION_VALUE_RANGE')
    );
  }

  if (!isVariableMappingReady.value) {
    errors.push(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.VARIABLE_MAPPING_REQUIRED')
    );
  }

  return errors;
});

const audienceValidationError = computed(() => {
  if (!requiresAudienceValidation.value) return '';
  if (wizardAudienceConfigErrors.value.length) return '';
  if (!audienceValidation.value?.validated) {
    return t(
      'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_VALIDATION_REQUIRED'
    );
  }
  if (Number(audienceValidation.value?.count || 0) <= 0) {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_VALIDATION_BLOCKED');
  }

  return '';
});

const canValidateAudience = computed(() => {
  if (!requiresAudienceValidation.value) return false;
  return wizardAudienceConfigErrors.value.length === 0;
});

const wizardStepErrors = computed(() => {
  const errors = [];

  if (wizardStep.value === 1) {
    if (!String(wizard.name || '').trim()) {
      errors.push(t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_NAME'));
    }
    if (!wizard.inbox_id) {
      errors.push(t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_INBOX'));
    }
  }

  if (wizardStep.value === 2 && !wizard.template_id) {
    errors.push(t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_TEMPLATE'));
  }

  if (wizardStep.value === 3) {
    errors.push(...wizardAudienceConfigErrors.value);
    if (audienceValidationError.value) {
      errors.push(audienceValidationError.value);
    }
  }

  if (wizardStep.value === 5 && !wizard.send_now && !wizard.scheduled_at) {
    errors.push(t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ERROR_SCHEDULED_AT'));
  }

  return errors;
});

const canGoNext = computed(() => {
  return wizardStepErrors.value.length === 0;
});

const openNewCampaignPage = () => {
  router.push(accountScopedRoute('campaigns_whatsapp_new'));
};

function resetVariableMappings() {
  Object.keys(variableMappings).forEach(key => {
    delete variableMappings[key];
  });
}

const MEDIA_ACCEPT_MAP = {
  image: 'image/jpeg,image/png,image/webp',
  video: 'video/mp4,video/3gpp',
  audio: 'audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg',
  document:
    'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

function acceptForVariable(variable) {
  return MEDIA_ACCEPT_MAP[variable.value_type] || '*/*';
}

async function uploadVariableMedia(variableKey, file) {
  if (!file) return;
  uploadingMediaKey.value = variableKey;
  try {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await WhatsappCampaignsAPI.uploadCampaignVariableMedia(
      wizard.inbox_id,
      formData
    );
    variableMappings[variableKey].value = data.media_id;
    variableMappings[variableKey].type = 'fixed';
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.MEDIA_UPLOAD_ERROR'));
  } finally {
    uploadingMediaKey.value = null;
  }
}

const resetWizard = () => {
  wizardStep.value = 1;
  wizardOpen.value = false;
  wizardNextAttempted.value = false;
  Object.assign(wizard, {
    name: '',
    inbox_id: '',
    template_id: '',
    audience_type: 'upload',
    send_now: true,
    scheduled_at: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    batch_size: 50,
    delay_type: 'fixed',
    delay_seconds: 2,
    delay_min_seconds: 1,
    delay_max_seconds: 5,
    window_start: '09:00',
    window_end: '18:00',
    retry_max_attempts: 3,
    retry_backoff_seconds: 30,
    upload_file: null,
    upload_phone_column: 'phone',
    import_inbox_id: '',
    pipeline_id: '',
    pipeline_stage_id: '',
    filter_labels: '',
    conversion_type_id: '',
    conversion_source_type: '',
    conversion_match_rule: 'any',
    conversion_converted_after: '',
    conversion_converted_before: '',
    conversion_days_since_min: '',
    conversion_days_since_max: '',
    conversion_min_value: '',
    conversion_max_value: '',
  });
  inboxTemplates.value = [];
  pipelineStages.value = [];
  uploadColumns.value = [];
  uploadSampleRow.value = {};
  audienceFields.value = [];
  previewData.value = null;
  audienceValidation.value = null;
  wizardExclusions.value = [];
  exclusionStagesMap.value = {};
  resetVariableMappings();
};

const closeWizard = () => {
  if (isCreateMode.value) {
    resetWizard();
    router.push(accountScopedRoute('campaigns_whatsapp_index'));
    return;
  }

  resetWizard();
};

const campaignSendSettings = campaign => {
  const sendSettings = campaign?.send_settings || {};
  return {
    batch_size: Number(sendSettings.batch_size || 50),
    delay_type: sendSettings.delay_type || 'fixed',
    delay_seconds: Number(sendSettings.delay_seconds || 2),
    delay_min_seconds: Number(sendSettings.delay_min_seconds || 1),
    delay_max_seconds: Number(sendSettings.delay_max_seconds || 5),
    window_start: sendSettings.window_start || '09:00',
    window_end: sendSettings.window_end || '18:00',
    retry_max_attempts: Number(sendSettings?.retry_policy?.max_attempts || 3),
    retry_backoff_seconds: Number(
      sendSettings?.retry_policy?.backoff_seconds || 30
    ),
    webhook_url: sendSettings.webhook_url || '',
    delivery_label_title: sendSettings.delivery_label_title || '',
  };
};

const campaignAudienceSettings = campaign => {
  const audienceFilters = campaign?.audience_filters || {};

  return {
    audience_type: campaign?.audience_type || 'upload',
    audience_filters: audienceFilters,
    import_inbox_id: String(audienceFilters.inbox_id || ''),
    filter_labels: Array(audienceFilters.labels || []).join(', '),
    pipeline_id: String(audienceFilters.pipeline_id || ''),
    pipeline_stage_id: String(audienceFilters.stage_id || ''),
    conversion_type_id: String(audienceFilters.conversion_type_id || ''),
    conversion_source_type: audienceFilters.source_type || '',
    conversion_match_rule: audienceFilters.match_rule || 'any',
    conversion_converted_after: audienceFilters.converted_after || '',
    conversion_converted_before: audienceFilters.converted_before || '',
    conversion_days_since_min: String(audienceFilters.days_since_min || ''),
    conversion_days_since_max: String(audienceFilters.days_since_max || ''),
    conversion_min_value: String(audienceFilters.min_value || ''),
    conversion_max_value: String(audienceFilters.max_value || ''),
  };
};

const conversionAudienceFiltersFromForm = form => ({
  conversion_type_id: form.conversion_type_id,
  source_type: form.conversion_source_type,
  match_rule: form.conversion_match_rule || 'any',
  converted_after: form.conversion_converted_after,
  converted_before: form.conversion_converted_before,
  days_since_min: form.conversion_days_since_min,
  days_since_max: form.conversion_days_since_max,
  min_value: form.conversion_min_value,
  max_value: form.conversion_max_value,
});

const compactValue = value => String(value || '').trim();

const normalizedConversionAudienceFilters = audienceFilterValues =>
  Object.entries(audienceFilterValues).reduce((acc, [key, value]) => {
    const normalized = compactValue(value);
    if (normalized) acc[key] = normalized;
    return acc;
  }, {});

const audienceSpecificFilterKeys = [
  'inbox_id',
  'labels',
  'pipeline_id',
  'stage_id',
  'conversion_type_id',
  'source_type',
  'match_rule',
  'converted_after',
  'converted_before',
  'days_since_min',
  'days_since_max',
  'min_value',
  'max_value',
];

const baseAudienceFilters = audienceFilterValues => {
  const nextFilters = { ...(audienceFilterValues || {}) };
  audienceSpecificFilterKeys.forEach(key => {
    delete nextFilters[key];
  });
  return nextFilters;
};

const conversionAudienceSummary = audienceFilterValues => {
  const normalized = normalizedConversionAudienceFilters(audienceFilterValues);
  const parts = [];
  const conversionType = conversionTypes.value.find(
    item => String(item.id) === String(normalized.conversion_type_id || '')
  );

  if (conversionType?.name) parts.push(conversionType.name);
  if (normalized.source_type)
    parts.push(sourceTypeLabel(normalized.source_type));
  if (normalized.match_rule) {
    parts.push(conversionMatchRuleLabel(normalized.match_rule));
  }
  if (normalized.days_since_min) {
    parts.push(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DAYS_MIN_SUMMARY', {
        count: normalized.days_since_min,
      })
    );
  }
  if (normalized.days_since_max) {
    parts.push(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DAYS_MAX_SUMMARY', {
        count: normalized.days_since_max,
      })
    );
  }
  if (normalized.converted_after) {
    parts.push(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DATE_FROM_SUMMARY', {
        date: normalized.converted_after,
      })
    );
  }
  if (normalized.converted_before) {
    parts.push(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DATE_TO_SUMMARY', {
        date: normalized.converted_before,
      })
    );
  }
  if (normalized.min_value) {
    parts.push(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_VALUE_MIN_SUMMARY', {
        value: normalized.min_value,
      })
    );
  }
  if (normalized.max_value) {
    parts.push(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_VALUE_MAX_SUMMARY', {
        value: normalized.max_value,
      })
    );
  }

  return parts.join(' • ') || '-';
};

function normalizedVariableMappings() {
  return templateVariables.value.reduce((acc, variable) => {
    const mapping = variableMappings[variable.key];
    if (!mapping) return acc;

    if (mapping.type === 'fixed') {
      acc[variable.key] = { type: 'fixed', value: mapping.value || '' };
      return acc;
    }

    acc[variable.key] = { type: mapping.type, key: mapping.key || '' };
    return acc;
  }, {});
}

function audienceFiltersFromWizard() {
  const audienceFilters = {
    variable_mappings: normalizedVariableMappings(),
  };

  if (wizard.audience_type === 'upload') {
    audienceFilters.phone_column = wizard.upload_phone_column;
  }

  if (wizard.audience_type === 'inbox_contacts') {
    audienceFilters.inbox_id = Number(wizard.import_inbox_id);
  }

  if (wizard.audience_type === 'filtered') {
    audienceFilters.labels = wizard.filter_labels
      .split(',')
      .map(label => label.trim())
      .filter(Boolean);
  }

  if (wizard.audience_type === 'pipeline_stage') {
    audienceFilters.pipeline_id = Number(wizard.pipeline_id);
    audienceFilters.stage_id = Number(wizard.pipeline_stage_id);
  }

  if (wizard.audience_type === 'conversion_filters') {
    Object.assign(
      audienceFilters,
      normalizedConversionAudienceFilters(
        conversionAudienceFiltersFromForm(wizard)
      )
    );
  }

  const validExclusions = wizardExclusions.value.filter(e => e.pipeline_id);
  if (validExclusions.length > 0) {
    audienceFilters.exclusions = validExclusions.map(e => ({
      pipeline_id: Number(e.pipeline_id),
      stage_ids: e.stage_ids.map(Number),
    }));
  }

  return audienceFilters;
}

function audienceFiltersFromSettings() {
  const validExclusions = settingsExclusions.value.filter(e => e.pipeline_id);
  const baseFilters = {
    ...baseAudienceFilters(settingsForm.audience_filters),
    ...(validExclusions.length > 0
      ? {
          exclusions: validExclusions.map(e => ({
            pipeline_id: Number(e.pipeline_id),
            stage_ids: e.stage_ids.map(Number),
          })),
        }
      : { exclusions: [] }),
  };

  if (settingsForm.audience_type === 'inbox_contacts') {
    return {
      ...baseFilters,
      inbox_id: Number(settingsForm.import_inbox_id) || undefined,
    };
  }

  if (settingsForm.audience_type === 'filtered') {
    return {
      ...baseFilters,
      labels: settingsForm.filter_labels
        .split(',')
        .map(label => label.trim())
        .filter(Boolean),
    };
  }

  if (settingsForm.audience_type === 'pipeline_stage') {
    return {
      ...baseFilters,
      pipeline_id: Number(settingsForm.pipeline_id) || undefined,
      stage_id: Number(settingsForm.pipeline_stage_id) || undefined,
    };
  }

  if (settingsForm.audience_type === 'conversion_filters') {
    return {
      ...baseFilters,
      ...normalizedConversionAudienceFilters(
        conversionAudienceFiltersFromForm(settingsForm)
      ),
    };
  }

  return baseFilters;
}

const campaignDelayTypeLabel = delayType => {
  const normalizedDelayType = String(delayType || '')
    .trim()
    .toLowerCase();

  if (normalizedDelayType === 'fixed') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_FIXED');
  }
  if (normalizedDelayType === 'random') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_RANDOM');
  }

  return humanizeValue(delayType) || '-';
};

const campaignAudienceTypeLabel = audienceType => {
  if (audienceType === 'inbox_contacts') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_INBOX');
  }
  if (audienceType === 'filtered') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_FILTERED');
  }
  if (audienceType === 'pipeline_stage') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_PIPELINE');
  }
  if (audienceType === 'conversion_filters') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_CONVERSIONS');
  }

  return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_UPLOAD');
};

const campaignAudienceSourceLabel = audienceType => {
  if (audienceType === 'inbox_contacts') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SOURCE_INBOX');
  }
  if (audienceType === 'filtered') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.FILTER_LABELS');
  }
  if (audienceType === 'pipeline_stage') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.STAGE');
  }
  if (audienceType === 'conversion_filters') {
    return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_FILTERS');
  }

  return t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.UPLOAD_FILE');
};

const campaignAudienceSourceValue = audienceSettings => {
  const inbox = whatsappInboxes.value.find(
    item => String(item.id) === String(audienceSettings.import_inbox_id)
  );
  const pipeline = pipelines.value.find(
    item => String(item.id) === String(audienceSettings.pipeline_id)
  );
  const stage = pipelineStages.value.find(
    item => String(item.id) === String(audienceSettings.pipeline_stage_id)
  );

  if (audienceSettings.audience_type === 'inbox_contacts') {
    return inbox?.name || audienceSettings.import_inbox_id || '-';
  }
  if (audienceSettings.audience_type === 'filtered') {
    return audienceSettings.filter_labels || '-';
  }
  if (audienceSettings.audience_type === 'pipeline_stage') {
    return [pipeline?.name, stage?.name].filter(Boolean).join(' / ') || '-';
  }
  if (audienceSettings.audience_type === 'conversion_filters') {
    return conversionAudienceSummary(audienceSettings);
  }

  return '-';
};

const selectedCampaignConfigRows = computed(() => {
  if (!selectedCampaign.value) return [];

  const sendSettings = campaignSendSettings(selectedCampaign.value);
  const audienceSettings = campaignAudienceSettings(selectedCampaign.value);

  return [
    {
      key: 'audience_type',
      label: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_TYPE'),
      value: campaignAudienceTypeLabel(audienceSettings.audience_type),
    },
    {
      key: 'audience_source',
      label: campaignAudienceSourceLabel(audienceSettings.audience_type),
      value: campaignAudienceSourceValue(audienceSettings),
    },
    {
      key: 'batch_size',
      label: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.BATCH_SIZE'),
      value: String(sendSettings.batch_size || '-'),
    },
    {
      key: 'delay_type',
      label: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_TYPE'),
      value: campaignDelayTypeLabel(sendSettings.delay_type),
    },
    {
      key: 'window_start',
      label: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WINDOW_START'),
      value: sendSettings.window_start || '-',
    },
    {
      key: 'window_end',
      label: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WINDOW_END'),
      value: sendSettings.window_end || '-',
    },
    {
      key: 'webhook_url',
      label: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WEBHOOK_URL'),
      value: sendSettings.webhook_url || '-',
    },
    {
      key: 'delivery_label_title',
      label: t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELIVERY_LABEL'),
      value: sendSettings.delivery_label_title || '-',
    },
  ];
});

const fetchPipelines = async () => {
  pipelinesLoading.value = true;
  try {
    const { data } = await PipelinesAPI.list();
    pipelines.value = data.payload || [];
  } catch (error) {
    pipelines.value = [];
  } finally {
    pipelinesLoading.value = false;
  }
};

const fetchConversionTypes = async () => {
  try {
    const { data } = await conversionTypesApi.list();
    conversionTypes.value = data.payload || [];
  } catch (error) {
    conversionTypes.value = [];
  }
};

const fetchPipelineStages = async pipelineId => {
  pipelineStages.value = [];
  if (!pipelineId) return;

  try {
    const { data } = await PipelinesAPI.listStages(pipelineId);
    pipelineStages.value = data.payload || [];
  } catch (error) {
    pipelineStages.value = [];
  }
};

const fetchWizardExclusionStages = async exclusion => {
  const pipelineId = exclusion.pipeline_id;
  exclusion.stage_ids = [];
  if (!pipelineId || exclusionStagesMap.value[pipelineId]) return;
  try {
    const { data } = await PipelinesAPI.listStages(pipelineId);
    exclusionStagesMap.value[pipelineId] = data.payload || [];
  } catch {
    exclusionStagesMap.value[pipelineId] = [];
  }
};

const fetchSettingsExclusionStages = async exclusion => {
  const pipelineId = exclusion.pipeline_id;
  exclusion.stage_ids = [];
  if (!pipelineId || settingsExclusionStagesMap.value[pipelineId]) return;
  try {
    const { data } = await PipelinesAPI.listStages(pipelineId);
    settingsExclusionStagesMap.value[pipelineId] = data.payload || [];
  } catch {
    settingsExclusionStagesMap.value[pipelineId] = [];
  }
};

const addWizardExclusion = () => {
  wizardExclusions.value.push({ pipeline_id: '', stage_ids: [] });
};

const removeWizardExclusion = index => {
  wizardExclusions.value.splice(index, 1);
};

const addSettingsExclusion = () => {
  settingsExclusions.value.push({ pipeline_id: '', stage_ids: [] });
};

const removeSettingsExclusion = index => {
  settingsExclusions.value.splice(index, 1);
};

const applyAudiencePrefillFromQuery = async () => {
  if (!isCreateMode.value) return;

  const audienceType = String(route.query.audience_type || '').trim();
  if (audienceType !== 'pipeline_stage') return;

  const pipelineId = Number(route.query.pipeline_id);
  const stageId = Number(route.query.stage_id || route.query.pipeline_stage_id);
  if (!pipelineId) return;

  wizard.audience_type = 'pipeline_stage';
  wizard.pipeline_id = String(pipelineId);

  await fetchPipelineStages(pipelineId);
  if (!stageId) return;

  const hasStage = pipelineStages.value.some(stage => stage.id === stageId);
  if (hasStage) {
    wizard.pipeline_stage_id = String(stageId);
  }
};

const fetchSummary = async ({ silent = false } = {}) => {
  try {
    const { data } = await WhatsappCampaignsAPI.getSummary({
      inbox_id: filters.inboxId || undefined,
      status: filters.status || undefined,
      q: filters.q || undefined,
    });
    summary.value = data;
  } catch (error) {
    if (!silent) {
      useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_LOAD_METRICS'));
    }
  }
};

const fetchCampaigns = async ({ silent = false } = {}) => {
  if (!silent) loading.value = true;
  try {
    const { data } = await WhatsappCampaignsAPI.get({
      inbox_id: filters.inboxId || undefined,
      status: filters.status || undefined,
      q: filters.q || undefined,
    });
    campaigns.value = data;
    if (!data.find(campaign => campaign.id === selectedCampaignId.value)) {
      selectedCampaignId.value = data[0]?.id || null;
    } else if (!selectedCampaignId.value && data.length) {
      selectedCampaignId.value = data[0].id;
    }
    await fetchSummary({ silent: true });
  } catch (error) {
    if (!silent) {
      useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_LOAD_CAMPAIGNS'));
    }
  } finally {
    if (!silent) loading.value = false;
  }
};

const fetchTemplates = async inboxId => {
  if (!inboxId) {
    inboxTemplates.value = [];
    return;
  }

  try {
    const { data } = await WhatsappCampaignsAPI.getTemplates(inboxId);
    inboxTemplates.value = data;
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_LOAD_TEMPLATES'));
  }
};

const syncTemplates = async () => {
  if (!wizard.inbox_id) return;

  syncingTemplates.value = true;
  try {
    await WhatsappCampaignsAPI.syncTemplates(wizard.inbox_id);
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.TEMPLATE_SYNC_STARTED'));
    await fetchTemplates(wizard.inbox_id);
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_SYNC_TEMPLATES'));
  } finally {
    syncingTemplates.value = false;
  }
};

const defaultMappingType = () =>
  wizard.audience_type === 'upload' ? 'column' : 'contact_field';

const syncVariableMappingsWithTemplate = () => {
  const variables = templateVariables.value;
  const keys = variables.map(v => v.key);

  Object.keys(variableMappings).forEach(key => {
    if (!keys.includes(key)) delete variableMappings[key];
  });

  variables.forEach(variable => {
    const { key } = variable;
    const isMedia = MEDIA_VALUE_TYPES.includes(variable.value_type);

    if (!variableMappings[key]) {
      variableMappings[key] = {
        type: isMedia ? 'fixed' : defaultMappingType(),
        key: '',
        value: '',
      };
      return;
    }

    if (isMedia) {
      variableMappings[key].type = 'fixed';
      return;
    }

    if (
      wizard.audience_type === 'upload' &&
      variableMappings[key].type === 'contact_field'
    ) {
      variableMappings[key].type = 'column';
      variableMappings[key].key = '';
    }

    if (
      wizard.audience_type !== 'upload' &&
      variableMappings[key].type === 'column'
    ) {
      variableMappings[key].type = 'contact_field';
      variableMappings[key].key = '';
    }
  });
};

const parseCsvLine = line => {
  const values = [];
  let buffer = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = line[index + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      buffer += '"';
      index += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(buffer.trim());
      buffer = '';
    } else {
      buffer += char;
    }
  }

  values.push(buffer.trim());
  return values;
};

const handleUploadFileChange = async event => {
  const file = event.target.files?.[0];
  wizard.upload_file = file || null;
  uploadColumns.value = [];
  uploadSampleRow.value = {};
  previewData.value = null;

  if (!file) return;

  const content = await file.text();
  const nonEmptyLines = content
    .split(/\r?\n/)
    .filter(line => line.trim().length > 0);

  if (!nonEmptyLines.length) return;

  const headers = parseCsvLine(nonEmptyLines[0]).filter(Boolean);
  uploadColumns.value = headers;

  if (nonEmptyLines.length > 1) {
    const rowValues = parseCsvLine(nonEmptyLines[1]);
    uploadSampleRow.value = headers.reduce((acc, header, index) => {
      acc[header] = rowValues[index] || '';
      return acc;
    }, {});
  }

  syncVariableMappingsWithTemplate();
};

const fetchAudienceFields = async inboxId => {
  audienceFields.value = [];
  if (!inboxId) return;

  try {
    const { data } = await WhatsappCampaignsAPI.getAudienceFields(inboxId);
    audienceFields.value = data.fields || [];
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_LOAD_AUDIENCE_FIELDS'));
  }
};

const requestTemplatePreview = async () => {
  if (!canPreviewTemplate.value) return;

  previewLoading.value = true;
  try {
    const payload = {
      audience_type: wizard.audience_type,
      variable_mappings: normalizedVariableMappings(),
    };

    if (wizard.audience_type === 'upload') {
      payload.sample_row = uploadSampleRow.value;
    }

    if (wizard.audience_type === 'inbox_contacts') {
      payload.source_inbox_id = Number(
        wizard.import_inbox_id || wizard.inbox_id
      );
    }

    if (wizard.audience_type === 'filtered') {
      payload.labels = wizard.filter_labels
        .split(',')
        .map(label => label.trim())
        .filter(Boolean);
    }

    if (wizard.audience_type === 'pipeline_stage') {
      payload.pipeline_id = Number(wizard.pipeline_id);
      payload.stage_id = Number(wizard.pipeline_stage_id);
    }

    if (wizard.audience_type === 'conversion_filters') {
      Object.assign(
        payload,
        normalizedConversionAudienceFilters(
          conversionAudienceFiltersFromForm(wizard)
        )
      );
    }

    const { data } = await WhatsappCampaignsAPI.previewTemplate(
      Number(wizard.inbox_id),
      Number(wizard.template_id),
      payload
    );
    previewData.value = data.preview;
    audienceValidation.value = data.preview?.audience || null;
  } catch (error) {
    previewData.value = null;
    audienceValidation.value = null;
    const backendError =
      error?.response?.data?.error ||
      t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_PREVIEW_TEMPLATE');
    useAlert(backendError);
  } finally {
    previewLoading.value = false;
  }
};

async function goToNextWizardStep() {
  wizardNextAttempted.value = true;

  if (
    wizardStep.value === 3 &&
    requiresAudienceValidation.value &&
    canValidateAudience.value &&
    !isAudienceValidationReady.value
  ) {
    await requestTemplatePreview();
  }

  if (canGoNext.value) {
    wizardStep.value += 1;
    wizardNextAttempted.value = false;
    return;
  }

  const [firstError] = wizardStepErrors.value;
  if (firstError) useAlert(firstError);
}

const createCampaign = async () => {
  if (!isAudienceValidationReady.value) {
    wizardStep.value = 3;
    useAlert(
      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_VALIDATION_REQUIRED')
    );
    return;
  }

  creating.value = true;
  try {
    const audienceFilters = audienceFiltersFromWizard();

    const sendSettings = {
      timezone: wizard.timezone,
      batch_size: Number(wizard.batch_size),
      delay_type: wizard.delay_type,
      delay_seconds: Number(wizard.delay_seconds),
      delay_min_seconds: Number(wizard.delay_min_seconds),
      delay_max_seconds: Number(wizard.delay_max_seconds),
      window_start: wizard.window_start,
      window_end: wizard.window_end,
      retry_policy: {
        max_attempts: Number(wizard.retry_max_attempts),
        backoff_seconds: Number(wizard.retry_backoff_seconds),
      },
      webhook_url: wizard.webhook_url?.trim() || '',
      delivery_label_title: wizard.delivery_label_title?.trim() || '',
    };

    const { data: campaign } = await WhatsappCampaignsAPI.create({
      campaign: {
        name: wizard.name,
        inbox_id: Number(wizard.inbox_id),
        audience_type: wizard.audience_type,
        audience_filters: audienceFilters,
        template_id: Number(wizard.template_id),
        template_name: selectedTemplate.value?.name || '',
        template_language: selectedTemplate.value?.language || 'en_US',
        send_settings: sendSettings,
      },
    });

    if (wizard.audience_type === 'upload' && wizard.upload_file) {
      const formData = new FormData();
      formData.append('file', wizard.upload_file);
      formData.append('phone_column', wizard.upload_phone_column);
      formData.append(
        'variable_mappings',
        JSON.stringify(normalizedVariableMappings())
      );
      await WhatsappCampaignsAPI.importRecipients(campaign.id, formData);
    }

    if (wizard.audience_type === 'inbox_contacts') {
      await WhatsappCampaignsAPI.recipientsFromInbox(campaign.id, {
        inbox_id: Number(wizard.import_inbox_id),
        variable_mappings: normalizedVariableMappings(),
      });
    }

    if (wizard.audience_type === 'filtered') {
      const labels = wizard.filter_labels
        .split(',')
        .map(label => label.trim())
        .filter(Boolean);
      await WhatsappCampaignsAPI.recipientsFromFilters(campaign.id, {
        labels,
        variable_mappings: normalizedVariableMappings(),
      });
    }

    if (wizard.audience_type === 'pipeline_stage') {
      await WhatsappCampaignsAPI.recipientsFromPipelineStage(campaign.id, {
        pipeline_id: Number(wizard.pipeline_id),
        stage_id: Number(wizard.pipeline_stage_id),
        variable_mappings: normalizedVariableMappings(),
      });
    }

    if (wizard.audience_type === 'conversion_filters') {
      await WhatsappCampaignsAPI.recipientsFromConversions(campaign.id, {
        ...normalizedConversionAudienceFilters(
          conversionAudienceFiltersFromForm(wizard)
        ),
        variable_mappings: normalizedVariableMappings(),
      });
    }

    if (wizard.send_now) {
      await WhatsappCampaignsAPI.start(campaign.id);
    } else if (wizard.scheduled_at) {
      await WhatsappCampaignsAPI.schedule(campaign.id, wizard.scheduled_at);
    }

    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.CAMPAIGN_CREATED'));
    closeWizard();
    await fetchCampaigns();
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_CREATE_CAMPAIGN'));
  } finally {
    creating.value = false;
  }
};

const updateStatusOptimistically = async (campaignId, nextStatus, action) => {
  const index = campaigns.value.findIndex(
    campaign => campaign.id === campaignId
  );
  if (index === -1) return;

  const previous = campaigns.value[index].status;
  campaigns.value[index].status = nextStatus;

  try {
    await action();
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.STATUS_UPDATED'));
    await Promise.all([
      fetchCampaigns({ silent: true }),
      fetchSummary({ silent: true }),
    ]);
  } catch (error) {
    campaigns.value[index].status = previous;
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_UPDATE_STATUS'));
  }
};

const startCampaign = campaignId =>
  updateStatusOptimistically(campaignId, 'running', () =>
    WhatsappCampaignsAPI.start(campaignId)
  );

const pauseCampaign = campaignId =>
  updateStatusOptimistically(campaignId, 'paused', () =>
    WhatsappCampaignsAPI.pause(campaignId)
  );

const resumeCampaign = campaignId =>
  updateStatusOptimistically(campaignId, 'running', () =>
    WhatsappCampaignsAPI.resume(campaignId)
  );

const cancelCampaign = campaignId =>
  updateStatusOptimistically(campaignId, 'cancelled', () =>
    WhatsappCampaignsAPI.cancel(campaignId)
  );

const fetchMetrics = async (campaignId, { silent = false } = {}) => {
  if (!campaignId) return;
  if (!silent) metricsLoading.value = true;
  try {
    const { data } = await WhatsappCampaignsAPI.getMetrics(campaignId);
    metrics.value = data;
  } catch (error) {
    if (!silent) {
      useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_LOAD_METRICS'));
    }
  } finally {
    if (!silent) metricsLoading.value = false;
  }
};

const fetchRecipients = async (
  campaignId,
  page = 1,
  { silent = false } = {}
) => {
  if (!campaignId) return;
  if (!silent) recipientsLoading.value = true;
  try {
    const { data } = await WhatsappCampaignsAPI.getRecipients(campaignId, {
      page,
    });
    recipients.value = data.payload;
    recipientsMeta.value = data.meta;
  } catch (error) {
    if (!silent) {
      useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_LOAD_RECIPIENTS'));
    }
  } finally {
    if (!silent) recipientsLoading.value = false;
  }
};

const fetchEvents = async (campaignId, { silent = false } = {}) => {
  if (!campaignId) return;
  if (!silent) eventsLoading.value = true;
  try {
    const { data } = await WhatsappCampaignsAPI.getEvents(campaignId);
    events.value = data.payload;
  } catch (error) {
    if (!silent) {
      useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_LOAD_EVENTS'));
    }
  } finally {
    if (!silent) eventsLoading.value = false;
  }
};

const exportCampaign = async campaignId => {
  try {
    const { data } = await WhatsappCampaignsAPI.exportCsv(campaignId);
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `whatsapp_campaign_${campaignId}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_EXPORT'));
  }
};

const openSettingsModal = async campaign => {
  if (!campaign) return;
  const settings = campaignSendSettings(campaign);
  const audience = campaignAudienceSettings(campaign);
  hydratingSettingsForm.value = true;
  Object.assign(settingsForm, settings, audience, {
    campaignId: campaign.id,
    name: campaign.name,
    status: campaign.status,
  });
  if (audience.pipeline_id) {
    await fetchPipelineStages(audience.pipeline_id);
  }
  const rawExclusions = Array(audience.audience_filters?.exclusions || []);
  settingsExclusions.value = rawExclusions.map(e => ({
    pipeline_id: String(e.pipeline_id || ''),
    stage_ids: Array(e.stage_ids || []),
  }));
  settingsExclusionStagesMap.value = {};
  const uniquePipelineIds = [
    ...new Set(
      settingsExclusions.value
        .map(exclusion => exclusion.pipeline_id)
        .filter(Boolean)
    ),
  ].filter(pipelineId => !settingsExclusionStagesMap.value[pipelineId]);

  await Promise.all(
    uniquePipelineIds.map(async pipelineId => {
      try {
        const { data } = await PipelinesAPI.listStages(pipelineId);
        settingsExclusionStagesMap.value[pipelineId] = data.payload || [];
      } catch {
        settingsExclusionStagesMap.value[pipelineId] = [];
      }
    })
  );
  settingsModalOpen.value = true;
  await nextTick();
  hydratingSettingsForm.value = false;
};

const saveCampaignSettings = async () => {
  if (!settingsForm.campaignId) return;

  savingSettings.value = true;
  try {
    const payload = {
      campaign: {
        name: settingsForm.name,
        send_settings: {
          batch_size: Number(settingsForm.batch_size),
          delay_type: settingsForm.delay_type,
          delay_seconds: Number(settingsForm.delay_seconds),
          delay_min_seconds: Number(settingsForm.delay_min_seconds),
          delay_max_seconds: Number(settingsForm.delay_max_seconds),
          window_start: settingsForm.window_start,
          window_end: settingsForm.window_end,
          retry_policy: {
            max_attempts: Number(settingsForm.retry_max_attempts),
            backoff_seconds: Number(settingsForm.retry_backoff_seconds),
          },
          webhook_url: settingsForm.webhook_url?.trim() || '',
          delivery_label_title: settingsForm.delivery_label_title?.trim() || '',
        },
      },
    };

    if (settingsForm.status === 'draft') {
      payload.campaign.audience_type = settingsForm.audience_type;
      payload.campaign.audience_filters = audienceFiltersFromSettings();
    }

    await WhatsappCampaignsAPI.update(settingsForm.campaignId, payload);
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.STATUS_UPDATED'));
    settingsModalOpen.value = false;
    await Promise.all([fetchCampaigns(), fetchSummary({ silent: true })]);
    if (selectedCampaignId.value) {
      await fetchMetrics(selectedCampaignId.value, { silent: true });
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_UPDATE_STATUS')
    );
  } finally {
    savingSettings.value = false;
  }
};

const cloneCampaign = async campaignId => {
  cloningCampaign.value = true;
  try {
    const { data } = await WhatsappCampaignsAPI.clone(campaignId);
    await Promise.all([fetchCampaigns(), fetchSummary({ silent: true })]);
    selectedCampaignId.value = data.id;
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.CAMPAIGN_CREATED'));
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_BROADCASTS.API.ERROR_CREATE_CAMPAIGN'));
  } finally {
    cloningCampaign.value = false;
  }
};

const deleteCampaign = async campaignId => {
  if (!campaignId) return;
  if (!window.confirm(t('CAMPAIGN.CONFIRM_DELETE.TITLE'))) return;

  deletingCampaignId.value = campaignId;
  try {
    await WhatsappCampaignsAPI.delete(campaignId);
    useAlert(t('CAMPAIGN.CONFIRM_DELETE.API.SUCCESS_MESSAGE'));

    if (selectedCampaignId.value === campaignId) {
      selectedCampaignId.value = null;
      metrics.value = null;
      recipients.value = [];
      events.value = [];
    }

    await Promise.all([fetchCampaigns(), fetchSummary({ silent: true })]);
  } catch (error) {
    useAlert(t('CAMPAIGN.CONFIRM_DELETE.API.ERROR_MESSAGE'));
  } finally {
    deletingCampaignId.value = null;
  }
};

const pollLiveData = async () => {
  await fetchCampaigns({ silent: true });
  await fetchSummary({ silent: true });
  if (!selectedCampaignId.value) return;

  await Promise.all([
    fetchMetrics(selectedCampaignId.value, { silent: true }),
    fetchRecipients(
      selectedCampaignId.value,
      recipientsMeta.value.current_page || 1,
      { silent: true }
    ),
    fetchEvents(selectedCampaignId.value, { silent: true }),
  ]);
};

const stopLivePolling = () => {
  if (livePollTimer) {
    clearInterval(livePollTimer);
    livePollTimer = null;
  }
};

const startLivePolling = () => {
  stopLivePolling();
  livePollTimer = setInterval(() => {
    pollLiveData();
  }, LIVE_POLL_MS);
};

const loadCampaignDetails = async campaignId => {
  await Promise.all([
    fetchMetrics(campaignId),
    fetchRecipients(campaignId, 1),
    fetchEvents(campaignId),
  ]);
};

watch(
  () => settingsForm.pipeline_id,
  pipelineId => {
    if (settingsForm.audience_type === 'pipeline_stage') {
      if (hydratingSettingsForm.value) {
        fetchPipelineStages(pipelineId);
        return;
      }

      settingsForm.pipeline_stage_id = '';
      fetchPipelineStages(pipelineId);
    }
  }
);

watch(
  () => wizard.inbox_id,
  inboxId => {
    wizard.template_id = '';
    uploadColumns.value = [];
    uploadSampleRow.value = {};
    previewData.value = null;
    audienceValidation.value = null;
    resetVariableMappings();
    fetchTemplates(inboxId);
    if (
      wizard.audience_type === 'filtered' ||
      wizard.audience_type === 'pipeline_stage' ||
      wizard.audience_type === 'conversion_filters'
    ) {
      fetchAudienceFields(inboxId);
    }
  }
);

watch(
  () => wizard.template_id,
  () => {
    previewData.value = null;
    audienceValidation.value = null;
    syncVariableMappingsWithTemplate();
  }
);

watch(
  () => wizardStep.value,
  () => {
    wizardNextAttempted.value = false;
  }
);

watch(
  () => wizard.audience_type,
  audienceType => {
    previewData.value = null;
    audienceValidation.value = null;
    syncVariableMappingsWithTemplate();

    if (audienceType === 'upload') return;

    if (audienceType === 'inbox_contacts') {
      fetchAudienceFields(wizard.import_inbox_id || wizard.inbox_id);
      return;
    }

    if (audienceType === 'pipeline_stage') {
      fetchAudienceFields(wizard.inbox_id);
      fetchPipelineStages(wizard.pipeline_id);
      return;
    }

    if (audienceType === 'conversion_filters') {
      fetchAudienceFields(wizard.inbox_id);
      return;
    }

    fetchAudienceFields(wizard.inbox_id);
  }
);

watch(
  () => wizard.import_inbox_id,
  sourceInboxId => {
    previewData.value = null;
    audienceValidation.value = null;
    if (wizard.audience_type === 'inbox_contacts') {
      fetchAudienceFields(sourceInboxId || wizard.inbox_id);
    }
  }
);

watch(
  () => wizard.filter_labels,
  () => {
    if (wizard.audience_type === 'filtered') {
      previewData.value = null;
      audienceValidation.value = null;
    }
  }
);

watch(
  () =>
    [
      wizard.conversion_type_id,
      wizard.conversion_source_type,
      wizard.conversion_match_rule,
      wizard.conversion_converted_after,
      wizard.conversion_converted_before,
      wizard.conversion_days_since_min,
      wizard.conversion_days_since_max,
      wizard.conversion_min_value,
      wizard.conversion_max_value,
    ].join(':'),
  () => {
    if (wizard.audience_type === 'conversion_filters') {
      previewData.value = null;
      audienceValidation.value = null;
    }
  }
);

watch(
  () => wizard.pipeline_id,
  pipelineId => {
    wizard.pipeline_stage_id = '';
    previewData.value = null;
    audienceValidation.value = null;
    if (wizard.audience_type === 'pipeline_stage') {
      fetchPipelineStages(pipelineId);
    }
  }
);

watch(
  () => JSON.stringify(normalizedVariableMappings()),
  () => {
    previewData.value = null;
    audienceValidation.value = null;
  }
);

watch(
  () => selectedCampaignId.value,
  campaignId => {
    if (campaignId) {
      loadCampaignDetails(campaignId);
    }
  }
);

watch(
  () => [isCreateMode.value, hasRunningCampaign.value].join(':'),
  value => {
    if (value === 'false:true') {
      startLivePolling();
      return;
    }

    stopLivePolling();
  },
  { immediate: true }
);

watch(
  () => route.name,
  name => {
    if (name === 'campaigns_whatsapp_new') {
      wizardOpen.value = true;
      return;
    }

    wizardOpen.value = false;
  },
  { immediate: true }
);

watch(
  () =>
    [
      route.name,
      route.query.audience_type,
      route.query.pipeline_id,
      route.query.stage_id,
      route.query.pipeline_stage_id,
    ].join(':'),
  async () => {
    await applyAudiencePrefillFromQuery();
  }
);

onMounted(async () => {
  await store.dispatch('inboxes/get');
  await store.dispatch('labels/get');
  await Promise.all([
    fetchCampaigns(),
    fetchPipelines(),
    fetchConversionTypes(),
    fetchSummary(),
  ]);
  await applyAudiencePrefillFromQuery();
});

onBeforeUnmount(() => {
  stopLivePolling();
});
</script>

<template>
  <CampaignLayout
    :header-title="
      isCreateMode
        ? t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CREATE')
        : t('CAMPAIGN.WHATSAPP_BROADCASTS.HEADER_TITLE')
    "
    :button-label="t('CAMPAIGN.WHATSAPP_BROADCASTS.NEW_CAMPAIGN')"
    :show-action-button="!isCreateMode"
    @click="openNewCampaignPage"
  >
    <template v-if="isCreateMode" #hero>
      <p class="text-sm text-n-slate-11">
        {{
          isCreateMode
            ? t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.TEMPLATES_HELP')
            : t('CAMPAIGN.NEW_PAGE.WHATSAPP.DESCRIPTION')
        }}
      </p>
    </template>
    <div class="space-y-5 py-4">
      <div
        v-if="!isCreateMode"
        class="flex gap-1 border-b border-n-alpha-3 pb-1"
      >
        <router-link
          :to="{ name: 'campaigns_whatsapp_index' }"
          class="rounded-lg px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
          active-class="bg-n-alpha-2 text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.CAMPAIGNS') }}
        </router-link>
        <router-link
          :to="{ name: 'campaigns_whatsapp_templates' }"
          class="rounded-lg px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
          active-class="bg-n-alpha-2 text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.TEMPLATES_TAB') }}
        </router-link>
      </div>
      <div
        v-if="!isCreateMode"
        class="rounded-2xl border border-n-slate-4 bg-gradient-to-br from-n-slate-1 via-n-alpha-1 to-n-slate-2 p-4 shadow-sm"
      >
        <div class="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Input
            v-model="filters.q"
            :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.FILTERS.SEARCH')"
            :placeholder="
              t('CAMPAIGN.WHATSAPP_BROADCASTS.FILTERS.SEARCH_PLACEHOLDER')
            "
            @keydown.enter="fetchCampaigns"
          />

          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.FILTERS.INBOX') }}
            </label>
            <select
              v-model="filters.inboxId"
              class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
              @change="fetchCampaigns"
            >
              <option value="">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.FILTERS.ALL') }}
              </option>
              <option
                v-for="inbox in whatsappInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.FILTERS.STATUS') }}
            </label>
            <select
              v-model="filters.status"
              class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
              @change="fetchCampaigns"
            >
              <option value="">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.FILTERS.ALL') }}
              </option>
              <option
                v-for="status in statusOptions"
                :key="status"
                :value="status"
              >
                {{ translateStatus(status) }}
              </option>
            </select>
          </div>

          <div class="flex items-end">
            <Button
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.FILTERS.APPLY')"
              class="w-full"
              @click="fetchCampaigns"
            />
          </div>
        </div>
      </div>

      <div
        v-if="!isCreateMode"
        class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-7"
      >
        <div
          v-for="card in summaryCampaignCards"
          :key="`summary-campaign-${card.key}`"
          class="rounded-xl border border-n-slate-4 bg-n-slate-1 p-3 shadow-sm"
        >
          <div class="mb-2 flex items-center justify-between">
            <p class="text-xs text-n-slate-10">{{ card.label }}</p>
            <div class="h-2 w-16 overflow-hidden rounded-full bg-n-slate-4">
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="card.bg"
                :style="{ width: card.value ? '100%' : '0%' }"
              />
            </div>
          </div>
          <p class="text-xl font-semibold" :class="card.accent">
            {{ card.value }}
          </p>
        </div>

        <div
          v-for="card in summaryMetricCards"
          :key="`summary-${card.key}`"
          class="rounded-xl border border-n-slate-4 bg-n-slate-1 p-3 shadow-sm"
        >
          <div class="mb-2 flex items-center justify-between">
            <p class="text-xs text-n-slate-10">{{ card.label }}</p>
            <div class="h-2 w-16 overflow-hidden rounded-full bg-n-slate-4">
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="card.bg"
                :style="{
                  width: `${Math.min(
                    100,
                    Math.round((card.value / summaryBaseTotal) * 100)
                  )}%`,
                }"
              />
            </div>
          </div>
          <p class="text-xl font-semibold" :class="card.accent">
            {{ card.value }}
          </p>
        </div>
      </div>

      <div
        v-if="wizardOpen || isCreateMode"
        class="overflow-hidden rounded-2xl border border-n-slate-4 bg-n-slate-1 shadow-sm"
      >
        <div
          class="border-b border-n-slate-4 bg-gradient-to-r from-n-blue-2/50 via-transparent to-n-teal-2/40 p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-xs uppercase tracking-wide text-n-slate-10">
                {{
                  t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.STEP', {
                    step: wizardStep,
                  })
                }}
              </p>
              <p class="text-sm font-semibold text-n-slate-12">
                {{ wizardStepTitle }}
              </p>
            </div>
            <p class="text-xs font-medium text-n-slate-11">
              {{ `${Math.round(wizardProgress)}%` }}
            </p>
          </div>

          <div class="mt-3 h-2 overflow-hidden rounded-full bg-n-slate-4">
            <div
              class="h-full rounded-full bg-gradient-to-r from-n-blue-9 to-n-teal-9 transition-all duration-500 ease-out"
              :style="{ width: `${wizardProgress}%` }"
            />
          </div>

          <div class="mt-4 grid grid-cols-5 gap-2">
            <button
              v-for="step in wizardSteps"
              :key="step.id"
              type="button"
              class="rounded-lg border px-2 py-2 text-left transition"
              :class="
                step.id === wizardStep
                  ? 'border-n-blue-7 bg-n-blue-2 text-n-blue-11'
                  : step.id < wizardStep
                    ? 'border-n-teal-6 bg-n-teal-2/70 text-n-teal-11'
                    : 'border-n-slate-4 bg-n-slate-2 text-n-slate-10 hover:bg-n-slate-3'
              "
              @click="wizardStep = step.id <= wizardStep ? step.id : wizardStep"
            >
              <p class="text-[11px] font-medium">
                {{ step.title }}
              </p>
            </button>
          </div>
        </div>

        <div class="p-4">
          <Transition
            mode="out-in"
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="translate-y-2 opacity-0"
            leave-active-class="transition-all duration-200 ease-in"
            leave-to-class="-translate-y-1 opacity-0"
          >
            <div :key="wizardStep" class="space-y-3">
              <div
                v-if="wizardStep === 1"
                class="grid grid-cols-1 gap-3 md:grid-cols-2"
              >
                <Input
                  v-model="wizard.name"
                  :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.NAME')"
                  :placeholder="
                    t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.NAME_PLACEHOLDER')
                  "
                />
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.INBOX') }}
                  </label>
                  <select
                    v-model="wizard.inbox_id"
                    class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                  >
                    <option value="">
                      {{
                        t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SELECT_INBOX')
                      }}
                    </option>
                    <option
                      v-for="inbox in whatsappInboxes"
                      :key="inbox.id"
                      :value="inbox.id"
                    >
                      {{ inbox.name }}
                    </option>
                  </select>
                </div>
              </div>

              <div v-else-if="wizardStep === 2" class="space-y-3">
                <div
                  class="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-n-slate-2 p-3"
                >
                  <p class="text-sm text-n-slate-11">
                    {{
                      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.TEMPLATES_HELP')
                    }}
                  </p>
                  <Button
                    :label="
                      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SYNC_TEMPLATES')
                    "
                    size="sm"
                    :is-loading="syncingTemplates"
                    @click="syncTemplates"
                  />
                </div>

                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.TEMPLATE') }}
                  </label>
                  <div
                    v-if="templateCards.length"
                    class="grid grid-cols-1 gap-2 md:grid-cols-2"
                  >
                    <button
                      v-for="template in templateCards"
                      :key="template.id"
                      type="button"
                      class="rounded-xl border p-3 text-left transition"
                      :class="
                        Number(wizard.template_id) === template.id
                          ? 'border-n-blue-8 bg-n-blue-2/30 ring-1 ring-n-blue-7/40'
                          : 'border-n-slate-4 bg-n-slate-1 hover:border-n-blue-7/40'
                      "
                      @click="wizard.template_id = String(template.id)"
                    >
                      <div class="mb-1 flex items-start justify-between gap-2">
                        <p
                          class="truncate text-sm font-semibold text-n-slate-12"
                        >
                          {{ template.name }}
                        </p>
                        <span
                          class="shrink-0 rounded-full bg-n-slate-2 px-2 py-0.5 text-[11px] text-n-slate-11"
                        >
                          {{ template.statusLabel }}
                        </span>
                      </div>
                      <p class="mb-2 text-[11px] text-n-slate-10">
                        {{ `${template.language} • ${template.category}` }}
                      </p>
                      <p
                        class="line-clamp-3 text-xs text-n-slate-12"
                        :title="template.previewText"
                      >
                        {{
                          template.previewText ||
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.TEMPLATE_PREVIEW_EMPTY'
                          )
                        }}
                      </p>
                      <p
                        v-if="template.previewExample"
                        class="mt-1 line-clamp-1 text-xs text-n-slate-10"
                        :title="template.previewExample"
                      >
                        {{ template.previewExample }}
                      </p>
                      <p class="mt-2 text-[11px] text-n-slate-10">
                        {{
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.TEMPLATE_VARIABLES_COUNT',
                            { count: template.variableCount }
                          )
                        }}
                      </p>
                    </button>
                  </div>
                  <p v-else class="text-xs text-n-slate-10">
                    {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.NO_TEMPLATES') }}
                  </p>
                </div>
              </div>

              <div v-else-if="wizardStep === 3" class="space-y-3">
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_TYPE') }}
                  </label>
                  <select
                    v-model="wizard.audience_type"
                    class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                  >
                    <option value="upload">
                      {{
                        t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_UPLOAD')
                      }}
                    </option>
                    <option value="inbox_contacts">
                      {{
                        t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_INBOX')
                      }}
                    </option>
                    <option value="pipeline_stage">
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_PIPELINE'
                        )
                      }}
                    </option>
                    <option value="conversion_filters">
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_CONVERSIONS'
                        )
                      }}
                    </option>
                    <option value="filtered">
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_FILTERED'
                        )
                      }}
                    </option>
                  </select>
                </div>

                <div
                  v-if="wizard.audience_type === 'upload'"
                  class="grid grid-cols-1 gap-3 rounded-xl bg-n-slate-2 p-3 md:grid-cols-2"
                >
                  <Input
                    type="file"
                    :label="
                      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.UPLOAD_FILE')
                    "
                    accept=".csv"
                    @change="handleUploadFileChange"
                  />
                  <Input
                    v-model="wizard.upload_phone_column"
                    :label="
                      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.PHONE_COLUMN')
                    "
                  />
                  <p class="text-xs text-n-slate-10 md:col-span-2">
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.COLUMNS_DETECTED',
                        {
                          count: uploadColumns.length,
                        }
                      )
                    }}
                  </p>
                </div>

                <div
                  v-if="wizard.audience_type === 'inbox_contacts'"
                  class="space-y-2 rounded-xl bg-n-slate-2 p-3"
                >
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SOURCE_INBOX') }}
                  </label>
                  <select
                    v-model="wizard.import_inbox_id"
                    class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                  >
                    <option value="">
                      {{
                        t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SELECT_INBOX')
                      }}
                    </option>
                    <option
                      v-for="inbox in whatsappInboxes"
                      :key="inbox.id"
                      :value="inbox.id"
                    >
                      {{ inbox.name }}
                    </option>
                  </select>
                </div>

                <div
                  v-if="wizard.audience_type === 'filtered'"
                  class="space-y-2 rounded-xl bg-n-slate-2 p-3"
                >
                  <Input
                    v-model="wizard.filter_labels"
                    :label="
                      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.FILTER_LABELS')
                    "
                    :placeholder="
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.FILTER_LABELS_PLACEHOLDER'
                      )
                    "
                  />
                </div>

                <div
                  v-if="wizard.audience_type === 'pipeline_stage'"
                  class="grid grid-cols-1 gap-3 rounded-xl bg-n-slate-2 p-3 md:grid-cols-2"
                >
                  <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium text-n-slate-12">
                      {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.PIPELINE') }}
                    </label>
                    <select
                      v-model="wizard.pipeline_id"
                      class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                      :disabled="pipelinesLoading"
                    >
                      <option value="">
                        {{
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SELECT_PIPELINE'
                          )
                        }}
                      </option>
                      <option
                        v-for="pipeline in pipelines"
                        :key="pipeline.id"
                        :value="pipeline.id"
                      >
                        {{ pipeline.name }}
                      </option>
                    </select>
                  </div>

                  <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium text-n-slate-12">
                      {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.STAGE') }}
                    </label>
                    <select
                      v-model="wizard.pipeline_stage_id"
                      class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                      :disabled="!wizard.pipeline_id"
                    >
                      <option value="">
                        {{
                          t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SELECT_STAGE')
                        }}
                      </option>
                      <option
                        v-for="stage in pipelineStages"
                        :key="stage.id"
                        :value="stage.id"
                      >
                        {{ stage.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <div
                  v-if="wizard.audience_type === 'conversion_filters'"
                  class="grid grid-cols-1 gap-3 rounded-xl bg-n-slate-2 p-3 md:grid-cols-2"
                >
                  <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium text-n-slate-12">
                      {{
                        t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_TYPE')
                      }}
                    </label>
                    <select
                      v-model="wizard.conversion_type_id"
                      class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                    >
                      <option value="">
                        {{
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_TYPE_ALL'
                          )
                        }}
                      </option>
                      <option
                        v-for="conversionType in conversionTypes"
                        :key="conversionType.id"
                        :value="String(conversionType.id)"
                      >
                        {{ conversionType.name }}
                      </option>
                    </select>
                  </div>

                  <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium text-n-slate-12">
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_SOURCE'
                        )
                      }}
                    </label>
                    <select
                      v-model="wizard.conversion_source_type"
                      class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                    >
                      <option
                        v-for="option in conversionSourceOptions"
                        :key="option.value || 'all'"
                        :value="option.value"
                      >
                        {{ t(option.labelKey) }}
                      </option>
                    </select>
                  </div>

                  <div class="flex flex-col gap-1 md:col-span-2">
                    <label class="text-sm font-medium text-n-slate-12">
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_MATCH_RULE'
                        )
                      }}
                    </label>
                    <select
                      v-model="wizard.conversion_match_rule"
                      class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                    >
                      <option
                        v-for="option in conversionMatchRuleOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ t(option.labelKey) }}
                      </option>
                    </select>
                  </div>

                  <Input
                    v-model="wizard.conversion_days_since_min"
                    type="number"
                    min="0"
                    :label="
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DAYS_MIN'
                      )
                    "
                  />
                  <Input
                    v-model="wizard.conversion_days_since_max"
                    type="number"
                    min="0"
                    :label="
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DAYS_MAX'
                      )
                    "
                  />
                  <Input
                    v-model="wizard.conversion_converted_after"
                    type="date"
                    :label="
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DATE_FROM'
                      )
                    "
                  />
                  <Input
                    v-model="wizard.conversion_converted_before"
                    type="date"
                    :label="
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DATE_TO'
                      )
                    "
                  />
                  <Input
                    v-model="wizard.conversion_min_value"
                    type="number"
                    min="0"
                    step="0.01"
                    :label="
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_VALUE_MIN'
                      )
                    "
                  />
                  <Input
                    v-model="wizard.conversion_max_value"
                    type="number"
                    min="0"
                    step="0.01"
                    :label="
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_VALUE_MAX'
                      )
                    "
                  />
                  <p class="text-xs text-n-slate-10 md:col-span-2">
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_FILTERS_HELP'
                      )
                    }}
                  </p>
                </div>

                <div
                  v-if="requiresAudienceValidation"
                  class="space-y-3 rounded-xl border border-n-slate-4 bg-n-slate-1 p-3"
                >
                  <div
                    class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p class="text-sm font-semibold text-n-slate-12">
                        {{
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_VALIDATION_TITLE'
                          )
                        }}
                      </p>
                      <p class="text-xs text-n-slate-10">
                        {{
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_VALIDATION_HINT'
                          )
                        }}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      :label="
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.VALIDATE_AUDIENCE_BUTTON'
                        )
                      "
                      :is-loading="previewLoading"
                      :disabled="!canValidateAudience"
                      @click="requestTemplatePreview"
                    />
                  </div>

                  <p
                    v-if="
                      !canValidateAudience && wizardAudienceConfigErrors.length
                    "
                    class="text-xs text-n-ruby-11"
                  >
                    {{ wizardAudienceConfigErrors[0] }}
                  </p>

                  <div
                    v-if="audienceValidation"
                    class="rounded-lg border p-3"
                    :class="
                      audienceValidation.count > 0
                        ? 'border-n-teal-5 bg-n-teal-2/40'
                        : 'border-n-ruby-5 bg-n-ruby-2/40'
                    "
                  >
                    <p class="text-xs font-semibold text-n-slate-12">
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_VALIDATION_COUNT',
                          {
                            count: Number(audienceValidation.count || 0),
                          }
                        )
                      }}
                    </p>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ audienceValidation.message }}
                    </p>
                  </div>
                </div>

                <div
                  v-if="wizardNextAttempted && wizardStepErrors.length"
                  class="rounded-xl border border-n-ruby-5 bg-n-ruby-2/40 p-3"
                >
                  <p class="text-xs font-semibold text-n-ruby-11">
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.FIX_BEFORE_CONTINUE'
                      )
                    }}
                  </p>
                  <ul class="mt-2 space-y-1 text-xs text-n-ruby-11">
                    <li v-for="error in wizardStepErrors" :key="error">
                      {{ error }}
                    </li>
                  </ul>
                </div>

                <!-- Exclusion by pipeline -->
                <div
                  class="space-y-2 rounded-xl border border-n-slate-4 bg-n-slate-1 p-3"
                >
                  <div class="flex items-center justify-between gap-2">
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.EXCLUSIONS_TITLE'
                        )
                      }}
                    </p>
                    <button
                      type="button"
                      class="flex items-center gap-1 text-xs text-n-brand hover:opacity-80"
                      @click="addWizardExclusion"
                    >
                      <span class="i-lucide-plus size-3.5" />
                      {{
                        t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ADD_EXCLUSION')
                      }}
                    </button>
                  </div>
                  <p
                    v-if="wizardExclusions.length === 0"
                    class="text-xs text-n-slate-10"
                  >
                    {{
                      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.EXCLUSIONS_EMPTY')
                    }}
                  </p>
                  <div
                    v-for="(exclusion, index) in wizardExclusions"
                    :key="index"
                    class="flex items-start gap-2 rounded-lg border border-n-slate-4 bg-n-slate-2 p-2"
                  >
                    <div class="flex-1 space-y-2">
                      <select
                        v-model="exclusion.pipeline_id"
                        class="h-9 w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-2 text-sm outline-none transition focus:border-n-blue-8"
                        @change="fetchWizardExclusionStages(exclusion)"
                      >
                        <option value="">
                          {{
                            t(
                              'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SELECT_PIPELINE'
                            )
                          }}
                        </option>
                        <option
                          v-for="pipeline in pipelines"
                          :key="pipeline.id"
                          :value="pipeline.id"
                        >
                          {{ pipeline.name }}
                        </option>
                      </select>
                      <div
                        v-if="
                          exclusion.pipeline_id &&
                          exclusionStagesMap[exclusion.pipeline_id]?.length
                        "
                      >
                        <p class="mb-1.5 text-xs text-n-slate-11">
                          {{
                            t(
                              'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.EXCLUSION_STAGES_HINT'
                            )
                          }}
                        </p>
                        <div class="flex flex-wrap gap-2">
                          <label
                            v-for="stage in exclusionStagesMap[
                              exclusion.pipeline_id
                            ]"
                            :key="stage.id"
                            class="flex cursor-pointer items-center gap-1.5 rounded-md border border-n-slate-4 bg-n-slate-1 px-2 py-1 text-xs hover:border-n-blue-8"
                          >
                            <input
                              v-model="exclusion.stage_ids"
                              type="checkbox"
                              :value="stage.id"
                              class="size-3"
                            />
                            {{ stage.name }}
                          </label>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="mt-1 text-n-ruby-10 hover:text-n-ruby-12"
                      @click="removeWizardExclusion(index)"
                    >
                      <span class="i-lucide-x size-4" />
                    </button>
                  </div>
                </div>
                <!-- End exclusion by pipeline -->

                <div
                  v-if="hasTemplateVariables"
                  class="space-y-3 rounded-xl border border-n-slate-4 bg-n-slate-1 p-3"
                >
                  <div class="flex items-center justify-between gap-2">
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.VARIABLE_MAPPING_TITLE'
                        )
                      }}
                    </p>
                    <span class="text-xs text-n-slate-10">
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.VARIABLE_MAPPING_COUNT',
                          {
                            count: templateVariables.length,
                          }
                        )
                      }}
                    </span>
                  </div>

                  <div class="space-y-2">
                    <div
                      v-for="item in templateVariablesWithMappings"
                      :key="item.variable.key"
                      class="grid grid-cols-1 gap-2 rounded-lg border border-n-slate-4 bg-n-slate-2 p-3 md:grid-cols-3"
                    >
                      <div class="md:col-span-1">
                        <p class="text-xs font-semibold text-n-slate-12">
                          {{ item.variable.key }}
                        </p>
                        <p class="text-[11px] text-n-slate-10">
                          {{ item.variable.placeholder }}
                        </p>
                      </div>

                      <template
                        v-if="
                          MEDIA_VALUE_TYPES.includes(item.variable.value_type)
                        "
                      >
                        <div class="flex items-end gap-2 md:col-span-2">
                          <div class="flex-1">
                            <label class="mb-1 block text-xs text-n-slate-11">
                              {{
                                t(
                                  'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.MEDIA_UPLOAD_LABEL',
                                  { type: item.variable.value_type }
                                )
                              }}
                            </label>
                            <div class="flex items-center gap-2">
                              <input
                                :ref="
                                  el => {
                                    if (el)
                                      mediaInputRefs[item.variable.key] = el;
                                    else
                                      delete mediaInputRefs[item.variable.key];
                                  }
                                "
                                type="file"
                                class="sr-only"
                                :accept="acceptForVariable(item.variable)"
                                @change="
                                  e =>
                                    uploadVariableMedia(
                                      item.variable.key,
                                      e.target.files[0]
                                    )
                                "
                              />
                              <Button
                                size="sm"
                                variant="faded"
                                color="slate"
                                icon="i-lucide-upload"
                                :label="
                                  t(
                                    'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.MEDIA_UPLOAD_BTN'
                                  )
                                "
                                :is-loading="
                                  uploadingMediaKey === item.variable.key
                                "
                                :disabled="
                                  uploadingMediaKey === item.variable.key
                                "
                                @click="
                                  mediaInputRefs[item.variable.key]?.click()
                                "
                              />
                              <span
                                v-if="item.mapping.value"
                                class="flex items-center gap-1 text-xs text-n-teal-11"
                              >
                                <span
                                  class="i-lucide-check-circle h-3.5 w-3.5"
                                />
                                {{
                                  t(
                                    'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.MEDIA_UPLOADED'
                                  )
                                }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </template>

                      <template v-else>
                        <div class="md:col-span-1">
                          <label class="mb-1 block text-xs text-n-slate-11">
                            {{
                              t(
                                'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.VARIABLE_SOURCE_TYPE'
                              )
                            }}
                          </label>
                          <select
                            v-model="item.mapping.type"
                            class="h-9 w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-2 text-xs outline-none transition focus:border-n-blue-8"
                          >
                            <option
                              :value="
                                wizard.audience_type === 'upload'
                                  ? 'column'
                                  : 'contact_field'
                              "
                            >
                              {{
                                wizard.audience_type === 'upload'
                                  ? t(
                                      'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SOURCE_COLUMN'
                                    )
                                  : t(
                                      'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SOURCE_CONTACT_FIELD'
                                    )
                              }}
                            </option>
                            <option value="fixed">
                              {{
                                t(
                                  'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SOURCE_FIXED'
                                )
                              }}
                            </option>
                          </select>
                        </div>

                        <div class="md:col-span-1">
                          <label class="mb-1 block text-xs text-n-slate-11">
                            {{
                              t(
                                'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.VARIABLE_SOURCE_VALUE'
                              )
                            }}
                          </label>
                          <select
                            v-if="item.mapping.type !== 'fixed'"
                            v-model="item.mapping.key"
                            class="h-9 w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-2 text-xs outline-none transition focus:border-n-blue-8"
                          >
                            <option value="">
                              {{
                                t(
                                  'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SELECT_SOURCE_VALUE'
                                )
                              }}
                            </option>
                            <option
                              v-for="option in mappingFieldOptions"
                              :key="option.key"
                              :value="option.key"
                            >
                              {{ option.label }}
                            </option>
                          </select>
                          <Input
                            v-else
                            v-model="item.mapping.value"
                            :placeholder="
                              t(
                                'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.FIXED_VALUE_PLACEHOLDER'
                              )
                            "
                          />
                        </div>
                      </template>
                    </div>
                  </div>

                  <p
                    v-if="!isVariableMappingReady"
                    class="text-xs text-n-ruby-11"
                  >
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.VARIABLE_MAPPING_REQUIRED'
                      )
                    }}
                  </p>

                  <div
                    class="rounded-lg border border-n-slate-4 bg-n-slate-2 p-3"
                  >
                    <div class="mb-2 flex items-center justify-between gap-2">
                      <p class="text-xs font-semibold text-n-slate-12">
                        {{
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.PREVIEW_SECTION_TITLE'
                          )
                        }}
                      </p>
                      <Button
                        size="xs"
                        :label="
                          requiresAudienceValidation
                            ? t(
                                'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.VALIDATE_AUDIENCE_BUTTON'
                              )
                            : t(
                                'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.PREVIEW_BUTTON'
                              )
                        "
                        :is-loading="previewLoading"
                        :disabled="!canPreviewTemplate"
                        @click="requestTemplatePreview"
                      />
                    </div>

                    <p
                      v-if="
                        wizard.audience_type === 'upload' && !wizard.upload_file
                      "
                      class="text-xs text-n-slate-10"
                    >
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.PREVIEW_UPLOAD_HINT'
                        )
                      }}
                    </p>

                    <p
                      v-if="
                        canPreviewTemplate && !previewData && !previewLoading
                      "
                      class="text-xs text-n-slate-10"
                    >
                      {{
                        requiresAudienceValidation
                          ? t(
                              'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_VALIDATION_HINT'
                            )
                          : t(
                              'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.PREVIEW_EMPTY_HINT'
                            )
                      }}
                    </p>

                    <div
                      v-if="requiresAudienceValidation && audienceValidation"
                      class="rounded-lg border p-3"
                      :class="
                        audienceValidation.count > 0
                          ? 'border-n-teal-5 bg-n-teal-2/40'
                          : 'border-n-ruby-5 bg-n-ruby-2/40'
                      "
                    >
                      <p class="text-xs font-semibold text-n-slate-12">
                        {{
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_VALIDATION_TITLE'
                          )
                        }}
                      </p>
                      <p class="mt-1 text-xs text-n-slate-11">
                        {{
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_VALIDATION_COUNT',
                            {
                              count: Number(audienceValidation.count || 0),
                            }
                          )
                        }}
                      </p>
                      <p class="mt-1 text-xs text-n-slate-10">
                        {{ audienceValidation.message }}
                      </p>
                    </div>

                    <p
                      v-if="
                        requiresAudienceValidation &&
                        !isAudienceValidationReady &&
                        audienceValidation
                      "
                      class="text-xs text-n-ruby-11"
                    >
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_VALIDATION_BLOCKED'
                        )
                      }}
                    </p>

                    <div
                      v-if="previewData"
                      class="space-y-2 rounded-lg border border-n-slate-4 bg-n-slate-1 p-3"
                    >
                      <p class="text-[11px] text-n-slate-10">
                        {{
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.PREVIEW_TEMPLATE_META',
                            {
                              name: previewData.template_name,
                              language: previewData.language,
                            }
                          )
                        }}
                      </p>
                      <p
                        v-if="previewData.context?.phone_number"
                        class="text-[11px] text-n-slate-10"
                      >
                        {{
                          t(
                            'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.PREVIEW_CONTACT_VALUE',
                            {
                              phone: previewData.context.phone_number,
                            }
                          )
                        }}
                      </p>
                      <div
                        v-for="component in previewData.rendered_components"
                        :key="`${component.type}-${component.text}`"
                        class="rounded-md bg-n-slate-2 px-3 py-2 text-xs text-n-slate-12"
                      >
                        <span
                          class="mr-1 font-semibold uppercase text-n-slate-10"
                        >
                          {{ `${component.type}:` }}
                        </span>
                        {{ component.text }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else-if="wizardStep === 4"
                class="grid grid-cols-1 gap-3 md:grid-cols-2"
              >
                <div class="flex flex-col gap-1 md:col-span-2">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_TYPE') }}
                  </label>
                  <select
                    v-model="wizard.delay_type"
                    class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                  >
                    <option value="fixed">
                      {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_FIXED') }}
                    </option>
                    <option value="random">
                      {{
                        t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_RANDOM')
                      }}
                    </option>
                  </select>
                </div>

                <Input
                  v-if="wizard.delay_type === 'fixed'"
                  v-model="wizard.delay_seconds"
                  type="number"
                  :label="
                    t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_SECONDS')
                  "
                />

                <Input
                  v-if="wizard.delay_type === 'random'"
                  v-model="wizard.delay_min_seconds"
                  type="number"
                  :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_MIN')"
                />

                <Input
                  v-if="wizard.delay_type === 'random'"
                  v-model="wizard.delay_max_seconds"
                  type="number"
                  :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_MAX')"
                />

                <div class="space-y-1">
                  <Input
                    v-model="wizard.batch_size"
                    type="number"
                    :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.BATCH_SIZE')"
                  />
                  <p class="text-xs text-n-amber-11">
                    {{
                      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.BATCH_SIZE_HELP')
                    }}
                  </p>
                </div>

                <Input
                  v-model="wizard.window_start"
                  type="time"
                  :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WINDOW_START')"
                />

                <Input
                  v-model="wizard.window_end"
                  type="time"
                  :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WINDOW_END')"
                />

                <Input
                  v-model="wizard.retry_max_attempts"
                  type="number"
                  :label="
                    t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.RETRY_ATTEMPTS')
                  "
                />

                <Input
                  v-model="wizard.retry_backoff_seconds"
                  type="number"
                  :label="
                    t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.RETRY_BACKOFF')
                  "
                />

                <div class="space-y-1 md:col-span-2">
                  <Input
                    v-model="wizard.webhook_url"
                    :label="
                      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WEBHOOK_URL')
                    "
                    :placeholder="
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WEBHOOK_URL_PLACEHOLDER'
                      )
                    "
                  />
                  <p class="text-xs text-n-slate-10">
                    {{
                      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WEBHOOK_URL_HELP')
                    }}
                  </p>
                </div>

                <div class="space-y-1 md:col-span-2">
                  <Input
                    v-model="wizard.delivery_label_title"
                    :label="
                      t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELIVERY_LABEL')
                    "
                    :placeholder="
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELIVERY_LABEL_PLACEHOLDER'
                      )
                    "
                  />
                  <p class="text-xs text-n-slate-10">
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELIVERY_LABEL_HELP'
                      )
                    }}
                  </p>
                  <p v-if="accountLabels.length" class="text-xs text-n-slate-9">
                    {{
                      accountLabels
                        .slice(0, 8)
                        .map(label => label.title)
                        .join(', ')
                    }}
                  </p>
                </div>
              </div>

              <div v-else class="space-y-3">
                <div class="rounded-xl bg-n-slate-2 p-3">
                  <div class="flex items-center gap-2">
                    <input
                      id="send_now"
                      v-model="wizard.send_now"
                      type="checkbox"
                      class="h-4 w-4 rounded border-n-slate-6"
                    />
                    <label
                      for="send_now"
                      class="text-sm font-medium text-n-slate-12"
                    >
                      {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SEND_NOW') }}
                    </label>
                  </div>
                </div>

                <Input
                  v-if="!wizard.send_now"
                  v-model="wizard.scheduled_at"
                  type="datetime-local"
                  :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SCHEDULED_AT')"
                />

                <Input
                  v-model="wizard.timezone"
                  :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.TIMEZONE')"
                />
              </div>
            </div>
          </Transition>

          <div
            class="mt-4 flex items-center justify-between gap-2 border-t border-n-slate-4 pt-4"
          >
            <Button
              v-if="wizardStep > 1"
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.BACK')"
              @click="wizardStep = wizardStep - 1"
            />
            <div v-else />
            <div class="ml-auto flex items-center gap-2">
              <Button
                variant="faded"
                color="slate"
                :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CANCEL')"
                @click="closeWizard"
              />
              <Button
                v-if="wizardStep < 5"
                :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.NEXT')"
                @click="goToNextWizardStep"
              />
              <Button
                v-else
                :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CREATE')"
                :is-loading="creating"
                @click="createCampaign"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="!isCreateMode" class="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div class="lg:col-span-5">
          <div
            class="rounded-2xl border border-n-slate-4 bg-n-slate-1 shadow-sm"
          >
            <div class="border-b border-n-slate-4 p-4">
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.HEADER_TITLE') }}
              </p>
            </div>

            <div v-if="loading" class="flex items-center justify-center py-8">
              <Spinner />
            </div>

            <div v-else class="max-h-[70vh] overflow-auto">
              <button
                v-for="campaign in campaigns"
                :key="campaign.id"
                type="button"
                class="m-2 flex w-[calc(100%-1rem)] flex-col gap-3 rounded-xl border p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm"
                :class="
                  selectedCampaignId === campaign.id
                    ? 'border-n-blue-6 bg-gradient-to-br from-n-blue-2/40 to-n-slate-1'
                    : 'border-n-slate-4 bg-n-slate-1 hover:bg-n-slate-2'
                "
                @click="selectedCampaignId = campaign.id"
              >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ campaign.name }}
                    </p>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ campaignTemplateLabel(campaign) }}
                    </p>
                  </div>
                  <span
                    class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium"
                    :class="statusBadgeClass(campaign.status)"
                  >
                    <span
                      v-if="campaign.status === 'running'"
                      class="h-3 w-3 animate-spin rounded-full border border-current border-r-transparent"
                    />
                    {{ translateStatus(campaign.status) }}
                  </span>
                </div>

                <div class="flex flex-wrap gap-2">
                  <Button
                    v-if="
                      campaign.status === 'draft' ||
                      campaign.status === 'scheduled'
                    "
                    size="xs"
                    :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.ACTIONS.START')"
                    @click.stop="startCampaign(campaign.id)"
                  />
                  <Button
                    v-if="campaign.status === 'running'"
                    size="xs"
                    variant="faded"
                    color="slate"
                    :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.ACTIONS.PAUSE')"
                    @click.stop="pauseCampaign(campaign.id)"
                  />
                  <Button
                    v-if="campaign.status === 'paused'"
                    size="xs"
                    :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.ACTIONS.RESUME')"
                    @click.stop="resumeCampaign(campaign.id)"
                  />
                  <Button
                    v-if="
                      ['draft', 'scheduled', 'running', 'paused'].includes(
                        campaign.status
                      )
                    "
                    size="xs"
                    variant="faded"
                    color="ruby"
                    :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.ACTIONS.CANCEL')"
                    @click.stop="cancelCampaign(campaign.id)"
                  />
                  <Button
                    size="xs"
                    variant="faded"
                    color="slate"
                    :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.ACTIONS.EDIT')"
                    @click.stop="openSettingsModal(campaign)"
                  />
                  <Button
                    v-if="
                      ['finished', 'cancelled', 'failed'].includes(
                        campaign.status
                      )
                    "
                    size="xs"
                    variant="faded"
                    color="slate"
                    :is-loading="cloningCampaign"
                    :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.ACTIONS.CLONE')"
                    @click.stop="cloneCampaign(campaign.id)"
                  />
                  <Button
                    size="xs"
                    variant="faded"
                    color="ruby"
                    icon="i-lucide-trash-2"
                    :is-loading="deletingCampaignId === campaign.id"
                    :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.ACTIONS.DELETE')"
                    @click.stop="deleteCampaign(campaign.id)"
                  />
                </div>
              </button>
            </div>
          </div>
        </div>

        <div class="lg:col-span-7">
          <div v-if="selectedCampaign" class="space-y-4">
            <div
              class="rounded-2xl border border-n-slate-4 bg-gradient-to-r from-n-slate-1 via-n-blue-2/20 to-n-teal-2/20 p-4 shadow-sm"
            >
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="text-lg font-semibold text-n-slate-12">
                    {{ selectedCampaign.name }}
                  </p>
                  <p class="mt-1 text-xs text-n-slate-10">
                    {{ campaignTemplateLabel(selectedCampaign) }}
                  </p>
                </div>
                <span
                  class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium"
                  :class="statusBadgeClass(selectedCampaign.status)"
                >
                  <span
                    v-if="selectedCampaign.status === 'running'"
                    class="h-3 w-3 animate-spin rounded-full border border-current border-r-transparent"
                  />
                  {{ translateStatus(selectedCampaign.status) }}
                </span>
              </div>
            </div>

            <div
              class="rounded-2xl border border-n-slate-4 bg-n-slate-1 p-4 shadow-sm"
            >
              <div class="mb-3 flex items-center justify-between gap-2">
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.CONFIG') }}
                </p>
                <Button
                  size="sm"
                  variant="faded"
                  color="slate"
                  :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.ACTIONS.EDIT')"
                  @click="openSettingsModal(selectedCampaign)"
                />
              </div>
              <div
                class="grid grid-cols-1 gap-2 text-xs text-n-slate-11 md:grid-cols-2"
              >
                <div
                  v-for="configRow in selectedCampaignConfigRows"
                  :key="configRow.key"
                  class="min-w-0 rounded-xl border border-n-slate-4 bg-n-slate-2 px-3 py-2"
                >
                  <p
                    class="break-words text-[11px] uppercase tracking-wide text-n-slate-9"
                  >
                    {{ configRow.label }}
                  </p>
                  <p
                    class="mt-1 break-all text-sm font-semibold text-n-slate-12"
                  >
                    {{ configRow.value }}
                  </p>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3 md:grid-cols-5">
              <div
                v-for="card in metricCards"
                :key="card.key"
                class="rounded-xl border border-n-slate-4 bg-n-slate-1 p-3 shadow-sm transition hover:-translate-y-0.5"
              >
                <div class="mb-2 flex items-center justify-between">
                  <p class="text-xs text-n-slate-10">{{ card.label }}</p>
                  <div
                    class="h-2 w-16 overflow-hidden rounded-full bg-n-slate-4"
                  >
                    <div
                      class="h-full rounded-full transition-all duration-500"
                      :class="card.bg"
                      :style="{
                        width: `${Math.min(
                          100,
                          Math.round((card.value / metricBaseTotal) * 100)
                        )}%`,
                      }"
                    />
                  </div>
                </div>
                <p class="text-xl font-semibold" :class="card.accent">
                  {{ card.value }}
                </p>
              </div>
            </div>

            <div
              class="rounded-2xl border border-n-slate-4 bg-n-slate-1 p-4 shadow-sm"
            >
              <div class="mb-3 flex items-center justify-between gap-2">
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.RECIPIENTS') }}
                </p>
                <Button
                  size="sm"
                  variant="faded"
                  color="slate"
                  :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.EXPORT')"
                  @click="exportCampaign(selectedCampaign.id)"
                />
              </div>

              <div
                v-if="recipientsLoading || metricsLoading"
                class="flex items-center justify-center py-8"
              >
                <Spinner />
              </div>

              <div
                v-else
                class="max-h-80 overflow-auto rounded-xl border border-n-slate-4"
              >
                <table class="w-full text-left text-xs">
                  <thead class="sticky top-0 bg-n-slate-2">
                    <tr class="border-b border-n-slate-4 text-n-slate-10">
                      <th class="px-3 py-2">
                        {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.PHONE') }}
                      </th>
                      <th class="px-3 py-2">
                        {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.STATUS') }}
                      </th>
                      <th class="px-3 py-2">
                        {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.ERROR') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="recipient in recipients"
                      :key="recipient.id"
                      class="border-b border-n-slate-3 transition hover:bg-n-slate-2"
                    >
                      <td class="px-3 py-2 text-n-slate-12">
                        {{ recipient.phone_e164 }}
                      </td>
                      <td class="px-3 py-2">
                        <span
                          class="rounded-md px-2 py-1 text-[11px] font-medium"
                          :class="statusBadgeClass(recipient.status)"
                        >
                          {{ translateStatus(recipient.status) }}
                        </span>
                      </td>
                      <td class="px-3 py-2 text-n-slate-10">
                        {{ recipient.error_message || '-' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="mt-3 flex items-center justify-between">
                <p class="text-xs text-n-slate-10">
                  {{
                    t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.TOTAL', {
                      count: recipientsMeta.total_count,
                    })
                  }}
                </p>
                <div class="flex items-center gap-2">
                  <Button
                    size="xs"
                    variant="faded"
                    color="slate"
                    :disabled="recipientsMeta.current_page <= 1"
                    :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.PREV')"
                    @click="
                      fetchRecipients(
                        selectedCampaign.id,
                        recipientsMeta.current_page - 1
                      )
                    "
                  />
                  <Button
                    size="xs"
                    variant="faded"
                    color="slate"
                    :disabled="
                      recipientsMeta.current_page >= recipientsMeta.total_pages
                    "
                    :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.NEXT')"
                    @click="
                      fetchRecipients(
                        selectedCampaign.id,
                        recipientsMeta.current_page + 1
                      )
                    "
                  />
                </div>
              </div>
            </div>

            <div
              class="rounded-2xl border border-n-slate-4 bg-n-slate-1 p-4 shadow-sm"
            >
              <p class="mb-3 text-sm font-semibold text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.DASHBOARD.EVENTS') }}
              </p>

              <div
                v-if="eventsLoading"
                class="flex items-center justify-center py-6"
              >
                <Spinner />
              </div>
              <ul v-else class="space-y-2 text-xs text-n-slate-11">
                <li
                  v-for="event in events"
                  :key="event.id"
                  class="rounded-lg border border-n-slate-4 bg-n-slate-2 px-3 py-2"
                >
                  {{ eventRowLabel(event) }}
                </li>
              </ul>
            </div>
          </div>

          <div
            v-else
            class="rounded-2xl border border-dashed border-n-slate-5 bg-gradient-to-br from-n-slate-1 to-n-slate-2 p-8 text-sm text-n-slate-10"
          >
            {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.SELECT_PROMPT') }}
          </div>
        </div>
      </div>

      <div
        v-if="settingsModalOpen"
        class="fixed inset-0 z-40 flex items-center justify-center bg-n-alpha-black3 p-4"
      >
        <div
          class="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-n-slate-4 bg-n-slate-1 p-4 shadow-xl"
        >
          <div class="mb-4 flex items-center justify-between">
            <p class="text-sm font-semibold text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.ACTIONS.EDIT') }}
            </p>
            <Button
              size="sm"
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CANCEL')"
              @click="settingsModalOpen = false"
            />
          </div>

          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input
              v-model="settingsForm.name"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.NAME')"
            />
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_TYPE') }}
              </label>
              <select
                v-model="settingsForm.audience_type"
                class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                :disabled="settingsForm.status !== 'draft'"
              >
                <option value="upload">
                  {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_UPLOAD') }}
                </option>
                <option value="inbox_contacts">
                  {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_INBOX') }}
                </option>
                <option value="filtered">
                  {{
                    t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_FILTERED')
                  }}
                </option>
                <option value="pipeline_stage">
                  {{
                    t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_PIPELINE')
                  }}
                </option>
                <option value="conversion_filters">
                  {{
                    t(
                      'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_CONVERSIONS'
                    )
                  }}
                </option>
              </select>
            </div>
            <div
              v-if="settingsForm.audience_type === 'upload'"
              class="rounded-xl border border-dashed border-n-slate-5 bg-n-slate-2 px-3 py-2 text-xs text-n-slate-10 md:col-span-2"
            >
              {{
                t(
                  'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_UPLOAD_EDIT_HELP'
                )
              }}
            </div>
            <div
              v-if="settingsForm.audience_type === 'inbox_contacts'"
              class="flex flex-col gap-1"
            >
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SOURCE_INBOX') }}
              </label>
              <select
                v-model="settingsForm.import_inbox_id"
                class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                :disabled="settingsForm.status !== 'draft'"
              >
                <option value="">
                  {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SOURCE_INBOX') }}
                </option>
                <option
                  v-for="inbox in whatsappInboxes"
                  :key="inbox.id"
                  :value="String(inbox.id)"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </div>
            <Input
              v-if="settingsForm.audience_type === 'filtered'"
              v-model="settingsForm.filter_labels"
              :disabled="settingsForm.status !== 'draft'"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.FILTER_LABELS')"
              :placeholder="
                t(
                  'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.FILTER_LABELS_PLACEHOLDER'
                )
              "
            />
            <div
              v-if="settingsForm.audience_type === 'pipeline_stage'"
              class="flex flex-col gap-1"
            >
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.PIPELINE') }}
              </label>
              <select
                v-model="settingsForm.pipeline_id"
                class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                :disabled="settingsForm.status !== 'draft'"
              >
                <option value="">
                  {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SELECT_PIPELINE') }}
                </option>
                <option
                  v-for="pipeline in pipelines"
                  :key="pipeline.id"
                  :value="String(pipeline.id)"
                >
                  {{ pipeline.name }}
                </option>
              </select>
            </div>
            <div
              v-if="settingsForm.audience_type === 'pipeline_stage'"
              class="flex flex-col gap-1"
            >
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.STAGE') }}
              </label>
              <select
                v-model="settingsForm.pipeline_stage_id"
                class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                :disabled="
                  settingsForm.status !== 'draft' || !settingsForm.pipeline_id
                "
              >
                <option value="">
                  {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SELECT_STAGE') }}
                </option>
                <option
                  v-for="stage in pipelineStages"
                  :key="stage.id"
                  :value="String(stage.id)"
                >
                  {{ stage.name }}
                </option>
              </select>
            </div>
            <div
              v-if="settingsForm.audience_type === 'conversion_filters'"
              class="flex flex-col gap-1"
            >
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_TYPE') }}
              </label>
              <select
                v-model="settingsForm.conversion_type_id"
                class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                :disabled="settingsForm.status !== 'draft'"
              >
                <option value="">
                  {{
                    t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_TYPE_ALL')
                  }}
                </option>
                <option
                  v-for="conversionType in conversionTypes"
                  :key="conversionType.id"
                  :value="String(conversionType.id)"
                >
                  {{ conversionType.name }}
                </option>
              </select>
            </div>
            <div
              v-if="settingsForm.audience_type === 'conversion_filters'"
              class="flex flex-col gap-1"
            >
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_SOURCE') }}
              </label>
              <select
                v-model="settingsForm.conversion_source_type"
                class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                :disabled="settingsForm.status !== 'draft'"
              >
                <option
                  v-for="option in conversionSourceOptions"
                  :key="option.value || 'all-settings'"
                  :value="option.value"
                >
                  {{ t(option.labelKey) }}
                </option>
              </select>
            </div>
            <div
              v-if="settingsForm.audience_type === 'conversion_filters'"
              class="flex flex-col gap-1 md:col-span-2"
            >
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_MATCH_RULE')
                }}
              </label>
              <select
                v-model="settingsForm.conversion_match_rule"
                class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
                :disabled="settingsForm.status !== 'draft'"
              >
                <option
                  v-for="option in conversionMatchRuleOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ t(option.labelKey) }}
                </option>
              </select>
            </div>
            <Input
              v-if="settingsForm.audience_type === 'conversion_filters'"
              v-model="settingsForm.conversion_days_since_min"
              :disabled="settingsForm.status !== 'draft'"
              type="number"
              min="0"
              :label="
                t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DAYS_MIN')
              "
            />
            <Input
              v-if="settingsForm.audience_type === 'conversion_filters'"
              v-model="settingsForm.conversion_days_since_max"
              :disabled="settingsForm.status !== 'draft'"
              type="number"
              min="0"
              :label="
                t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DAYS_MAX')
              "
            />
            <Input
              v-if="settingsForm.audience_type === 'conversion_filters'"
              v-model="settingsForm.conversion_converted_after"
              :disabled="settingsForm.status !== 'draft'"
              type="date"
              :label="
                t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DATE_FROM')
              "
            />
            <Input
              v-if="settingsForm.audience_type === 'conversion_filters'"
              v-model="settingsForm.conversion_converted_before"
              :disabled="settingsForm.status !== 'draft'"
              type="date"
              :label="
                t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_DATE_TO')
              "
            />
            <Input
              v-if="settingsForm.audience_type === 'conversion_filters'"
              v-model="settingsForm.conversion_min_value"
              :disabled="settingsForm.status !== 'draft'"
              type="number"
              min="0"
              step="0.01"
              :label="
                t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_VALUE_MIN')
              "
            />
            <Input
              v-if="settingsForm.audience_type === 'conversion_filters'"
              v-model="settingsForm.conversion_max_value"
              :disabled="settingsForm.status !== 'draft'"
              type="number"
              min="0"
              step="0.01"
              :label="
                t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_VALUE_MAX')
              "
            />
            <p
              v-if="settingsForm.audience_type === 'conversion_filters'"
              class="text-xs text-n-slate-10 md:col-span-2"
            >
              {{
                t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONVERSION_FILTERS_HELP')
              }}
            </p>
            <!-- Exclusion by pipeline (settings) -->
            <div
              class="space-y-2 rounded-xl border border-n-slate-4 bg-n-slate-1 p-3 md:col-span-2"
            >
              <div class="flex items-center justify-between gap-2">
                <p class="text-sm font-semibold text-n-slate-12">
                  {{
                    t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.EXCLUSIONS_TITLE')
                  }}
                </p>
                <button
                  type="button"
                  :disabled="settingsForm.status !== 'draft'"
                  class="flex items-center gap-1 text-xs text-n-brand hover:opacity-80 disabled:opacity-40"
                  @click="addSettingsExclusion"
                >
                  <span class="i-lucide-plus size-3.5" />
                  {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.ADD_EXCLUSION') }}
                </button>
              </div>
              <p
                v-if="settingsExclusions.length === 0"
                class="text-xs text-n-slate-10"
              >
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.EXCLUSIONS_EMPTY') }}
              </p>
              <div
                v-for="(exclusion, index) in settingsExclusions"
                :key="index"
                class="flex items-start gap-2 rounded-lg border border-n-slate-4 bg-n-slate-2 p-2"
              >
                <div class="flex-1 space-y-2">
                  <select
                    v-model="exclusion.pipeline_id"
                    :disabled="settingsForm.status !== 'draft'"
                    class="h-9 w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-2 text-sm outline-none transition focus:border-n-blue-8 disabled:opacity-60"
                    @change="fetchSettingsExclusionStages(exclusion)"
                  >
                    <option value="">
                      {{
                        t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.SELECT_PIPELINE')
                      }}
                    </option>
                    <option
                      v-for="pipeline in pipelines"
                      :key="pipeline.id"
                      :value="String(pipeline.id)"
                    >
                      {{ pipeline.name }}
                    </option>
                  </select>
                  <div
                    v-if="
                      exclusion.pipeline_id &&
                      settingsExclusionStagesMap[exclusion.pipeline_id]?.length
                    "
                  >
                    <p class="mb-1.5 text-xs text-n-slate-11">
                      {{
                        t(
                          'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.EXCLUSION_STAGES_HINT'
                        )
                      }}
                    </p>
                    <div class="flex flex-wrap gap-2">
                      <label
                        v-for="stage in settingsExclusionStagesMap[
                          exclusion.pipeline_id
                        ]"
                        :key="stage.id"
                        class="flex cursor-pointer items-center gap-1.5 rounded-md border border-n-slate-4 bg-n-slate-1 px-2 py-1 text-xs hover:border-n-blue-8"
                        :class="{
                          'opacity-60 pointer-events-none':
                            settingsForm.status !== 'draft',
                        }"
                      >
                        <input
                          v-model="exclusion.stage_ids"
                          type="checkbox"
                          :value="stage.id"
                          :disabled="settingsForm.status !== 'draft'"
                          class="size-3"
                        />
                        {{ stage.name }}
                      </label>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  :disabled="settingsForm.status !== 'draft'"
                  class="mt-1 text-n-ruby-10 hover:text-n-ruby-12 disabled:opacity-40"
                  @click="removeSettingsExclusion(index)"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>
            </div>
            <!-- End exclusion by pipeline (settings) -->

            <Input
              v-model="settingsForm.batch_size"
              type="number"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.BATCH_SIZE')"
            />
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_TYPE') }}
              </label>
              <select
                v-model="settingsForm.delay_type"
                class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
              >
                <option value="fixed">
                  {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_FIXED') }}
                </option>
                <option value="random">
                  {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_RANDOM') }}
                </option>
              </select>
            </div>
            <Input
              v-if="settingsForm.delay_type === 'fixed'"
              v-model="settingsForm.delay_seconds"
              type="number"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_SECONDS')"
            />
            <Input
              v-if="settingsForm.delay_type === 'random'"
              v-model="settingsForm.delay_min_seconds"
              type="number"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_MIN')"
            />
            <Input
              v-if="settingsForm.delay_type === 'random'"
              v-model="settingsForm.delay_max_seconds"
              type="number"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELAY_MAX')"
            />
            <Input
              v-model="settingsForm.window_start"
              type="time"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WINDOW_START')"
            />
            <Input
              v-model="settingsForm.window_end"
              type="time"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WINDOW_END')"
            />
            <Input
              v-model="settingsForm.retry_max_attempts"
              type="number"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.RETRY_ATTEMPTS')"
            />
            <Input
              v-model="settingsForm.retry_backoff_seconds"
              type="number"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.RETRY_BACKOFF')"
            />
            <div class="space-y-1 md:col-span-2">
              <p
                v-if="settingsForm.status !== 'draft'"
                class="text-xs text-n-amber-11"
              >
                {{
                  t(
                    'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.AUDIENCE_EDIT_DRAFT_ONLY'
                  )
                }}
              </p>
            </div>
            <div class="space-y-1 md:col-span-2">
              <Input
                v-model="settingsForm.webhook_url"
                :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WEBHOOK_URL')"
                :placeholder="
                  t(
                    'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WEBHOOK_URL_PLACEHOLDER'
                  )
                "
              />
              <p class="text-xs text-n-slate-10">
                {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.WEBHOOK_URL_HELP') }}
              </p>
            </div>
            <div class="space-y-1 md:col-span-2">
              <Input
                v-model="settingsForm.delivery_label_title"
                :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELIVERY_LABEL')"
                :placeholder="
                  t(
                    'CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELIVERY_LABEL_PLACEHOLDER'
                  )
                "
              />
              <p class="text-xs text-n-slate-10">
                {{
                  t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.DELIVERY_LABEL_HELP')
                }}
              </p>
            </div>
          </div>

          <div
            class="mt-4 flex items-center justify-end gap-2 border-t border-n-slate-4 pt-4"
          >
            <Button
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CANCEL')"
              @click="settingsModalOpen = false"
            />
            <Button
              :is-loading="savingSettings"
              :label="t('CAMPAIGN.WHATSAPP_BROADCASTS.ACTIONS.EDIT')"
              @click="saveCampaignSettings"
            />
          </div>
        </div>
      </div>
    </div>
  </CampaignLayout>
</template>
